import React, { useContext, useEffect, useMemo, useState } from 'react'
import i18n from 'simple-react-i18n'
import PropTypes from 'prop-types'
import LaunchRoundedIcon from '@mui/icons-material/LaunchRounded'
import { usedUrl } from 'conf/basepath'
import SuperMultiAutocomplete from 'components/forms/SuperMultiAutocomplete'
import { shallowEqual, useSelector } from 'react-redux'
import { chunk, keys, lowerCase, mapValues, minBy, omit, toUpper } from 'lodash'
import { Box, Card, CardContent, Chip, Grid2, IconButton, Popover, Stack, Switch, Tooltip, Typography } from '@mui/material'
import { CardTitle } from 'components/card/NewCard'
import useDebounce from 'utils/customHook/useDebounce'
import GlobalParametersDto from 'administration/dto/GlobalParameterDto'
import moment from 'moment'
import HelpRoundedIcon from '@mui/icons-material/HelpRounded'
import { MODULES, OBSERVATORY, WIDGET, WIDGET_CARTO } from './ObservatoryConstants'
import { ModuleHabsContext, ModuleHabsDispatchContext } from './AdminModulesApp'
import { LARGE_RADIUS } from 'utils/constants/Theme'
import TooltipMUI from 'components/styled/TooltipMUI'
import SimpleInput, { INPUT_TYPES } from 'components/forms/SimpleInput'
import ArrowOutwardOutlinedIcon from '@mui/icons-material/ArrowOutwardOutlined'
import { FieldsetMUI } from 'components/StyledElements'
import ShowChartRoundedIcon from '@mui/icons-material/ShowChartRounded'
import MultilineChartRoundedIcon from '@mui/icons-material/MultilineChartRounded'
import InsertChartOutlinedRoundedIcon from '@mui/icons-material/InsertChartOutlinedRounded'
import { brutRgbMainBlue, mainBlue, PASTEL_COLOR } from 'utils/constants/ColorTheme'
import SimpleCheckbox from 'components/forms/SimpleCheckbox'
import InfoRoundedIcon from '@mui/icons-material/InfoRounded'
import { getI18nOrLabel } from 'utils/StringUtil'
import WarningRoundedIcon from '@mui/icons-material/WarningRounded'
import QueryStatsRoundedIcon from '@mui/icons-material/QueryStatsRounded'
import NewRadioButtons from 'components/forms/NewRadioButtons'
import { CUMUL_PERSO_AVERAGE, CUMUL_PERSO_MAX, CUMUL_PERSO_MIN, CUMUL_PERSO_SUM } from 'utils/constants/MeasureConstants'
import MapRoundedIcon from '@mui/icons-material/MapRounded'
import ArrowOutwardRoundedIcon from '@mui/icons-material/ArrowOutwardRounded'
import PieChartRoundedIcon from '@mui/icons-material/PieChartRounded'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import LocationCityRoundedIcon from '@mui/icons-material/LocationCityRounded'
import useSandreList from 'utils/customHook/useSandreList'
import { SANDRE } from 'referencial/constants/ReferencialConstants'
import BuildRoundedIcon from '@mui/icons-material/BuildRounded'
import cmsTitleHelp from 'assets/pictures/observatory/cmsTitreHelp.webp'
import cmsHomeHelp from 'assets/pictures/observatory/cmsAccueilHelp.webp'
import cmsInfosHelp from 'assets/pictures/observatory/cmsInfosHelp.webp'
import cmsPartnersHelp from 'assets/pictures/observatory/cmsPartenairesHelp.webp'
import passwordPolicyHelp from 'assets/pictures/observatory/passwordPolicyHelp.webp'
import securityPasswordDescriptionHelp from 'assets/pictures/observatory/securityPasswordDescriptionHelp.webp'
import categoriesCmsActualitesHelp from 'assets/pictures/observatory/categoriesCmsActualitesHelp.webp'
import categoriesCmsDocumentsHelp from 'assets/pictures/observatory/categoriesCmsDocumentsHelp.webp'
import supportMailAddressHelp from 'assets/pictures/observatory/supportMailAddressHelp.webp'
import supportPhoneNumberHelp from 'assets/pictures/observatory/supportPhoneNumberHelp.webp'
import supportContactScheduleHelp from 'assets/pictures/observatory/supportContactScheduleHelp.webp'
import cmsSupportHelp from 'assets/pictures/observatory/cmsSupportHelp.webp'
import withStatsHelp from 'assets/pictures/observatory/withStatsHelp.webp'
import idDailyMinHelp from 'assets/pictures/observatory/idDailyMinHelp.webp'
import piezoValidOnlyHelp from 'assets/pictures/observatory/piezoValidOnlyHelp.webp'
import piezometryHorizonsHelp from 'assets/pictures/observatory/piezometryHorizonsHelp.webp'
import hydroValidOnlyHelp from 'assets/pictures/observatory/hydroValidOnlyHelp.webp'
import hydrometryHorizonsHelp from 'assets/pictures/observatory/hydrometryHorizonsHelp.webp'
import pluvioValidOnlyHelp from 'assets/pictures/observatory/pluvioValidOnlyHelp.webp'
import pluviometryHorizonsHelp from 'assets/pictures/observatory/pluviometryHorizonsHelp.webp'
import categorieCmsCGUHelp from 'assets/pictures/observatory/categorieCmsCGUHelp.webp'
import idRecaptchaContactHelp from 'assets/pictures/observatory/idRecaptchaContactHelp.webp'
import categoriesCmsFaqHelp from 'assets/pictures/observatory/categoriesCmsFaqHelp.webp'
import cmsReponsesHelp from 'assets/pictures/observatory/cmsReponsesHelp.webp'
import piezometryLimitHistoHelp from 'assets/pictures/observatory/piezometryLimitHistoHelp.webp'
import hydrometryLimitHistoHelp from 'assets/pictures/observatory/hydrometryLimitHistoHelp.webp'
import pluviometryLimitHistoHelp from 'assets/pictures/observatory/pluviometryLimitHistoHelp.webp'
import widgetPublicTokenHelp from 'assets/pictures/observatory/widgetPublicTokenHelp.webp'
import contactPageLinkHelp from 'assets/pictures/observatory/contactPageLinkHelp.webp'
import notDisplayWidgetHomePageHelp from 'assets/pictures/observatory/notDisplayWidgetHomePageHelp.webp'
import { H_DISTRIBUTION_MODULE, H_HYDRO_MODULE, H_INSTALLATION_MODULE, H_PIEZO_MODULE, H_PLUVIO_MODULE, H_PRODUCTION_MODULE, H_QUALITO_MODULE } from 'account/constants/AccessRulesConstants'
import HabilitationRequired from 'utils/components/HabilitationRequired'
import noData from 'assets/pictures/observatory/no-data.svg'

const ParameterCard = ({
    title,
    children,
}) => (
    <Card variant='newCard'>
        <CardTitle title={<Typography variant='cardTitle'>{title}</Typography>} />
        <CardContent>
            <Stack spacing={1.25} sx={{ '& .MuiAutocomplete-root': { margin: 0 }, '& .MuiTextField-root': { paddingTop: '0 !important' } }}>
                <Grid2 container justifyContent='flex-end' columnSpacing={1} sx={{ paddingBottom: 1.25 }}>
                    <Grid2 size={3.25}><Typography variant='formTitle'>{i18n.globalToTheApplication}</Typography></Grid2>
                    <Grid2 size={3.25}><Typography variant='formTitle'>{i18n.byModule}</Typography></Grid2>
                    <Grid2 size={0.5} />
                </Grid2>
                {children}
            </Stack>
        </CardContent>
    </Card>
)

ParameterCard.propTypes = {
    title: PropTypes.string,
    children: PropTypes.element,
}

const SeeCmsIconTooltip = () => {
    const {
        cmsCategories,
    } = useSelector(store => ({
        cmsCategories: store.EventsReducer.cmsCategories,
    }), shallowEqual)

    const categoryId = minBy(cmsCategories, cc => cc.id)?.id

    return (
        <Tooltip title={i18n.seeTheCms} placement='right'>
            <IconButton onClick={() => window.open(`${usedUrl}#/contents/${categoryId}`, '_blank').focus()}>
                <LaunchRoundedIcon />
            </IconButton>
        </Tooltip>
    )
}

const SeeCmsCategIconTooltip = () => (
    <Tooltip title={i18n.seeCmsCategories} placement='right'>
        <IconButton onClick={() => window.open(`${usedUrl}#/administration/contents`, '_blank').focus()}>
            <LaunchRoundedIcon />
        </IconButton>
    </Tooltip>
)

const SeeOtherIconTooltip = ({
    title = 'En voir plus',
    link = '',
}) => (
    <Tooltip title={title} placement='right'>
        <IconButton onClick={() => window.open(link, '_blank').focus()}>
            <LaunchRoundedIcon />
        </IconButton>
    </Tooltip>
)

SeeOtherIconTooltip.propTypes = {
    title: PropTypes.string,
    link: PropTypes.string,
}

const HelpParameter = ({
    image,
}) => (
    <TooltipMUI
        title={(
            <Box sx={{ '& img': { width: 'auto', height: '40vh', borderRadius: LARGE_RADIUS } }}>
                <img src={image} alt='Image détaillant le paramètre' />
            </Box>
        )}
        placement='right'
        sx={{ borderRadius: LARGE_RADIUS, padding: 0.5 }}
    >
        <HelpRoundedIcon />
    </TooltipMUI>
)

HelpParameter.propTypes = {
    image: PropTypes.string,
}

const CmsTitle = ({
    parameters = [],
    setParameters = () => {},
    getGlobalParameter = () => {},
    moduleParameters = [],
    setModuleParameters = [],
    getGlobalModuleParameter = () => {},
    cmsEvents = [],
}) => {
    const parameter = 'cmsTitre'
    const foundParameter = parameters.find(p => p.parameter === parameter)?.value
    const foundModuleParameter = moduleParameters.find(p => p.parameter === parameter)?.value

    const [cmsTitle, setCmsTitle] = useState(foundParameter && parseInt(foundParameter))
    const [moduleCmsTitle, setModuleCmsTitle] = useState(foundModuleParameter && parseInt(foundModuleParameter))

    useDebounce(() => {
        setParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalParameter(parameter, cmsTitle),
        ])
    }, 200, [cmsTitle])

    useDebounce(() => {
        setModuleParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalModuleParameter(parameter, moduleCmsTitle),
        ])
    }, 200, [moduleCmsTitle])

    return (
        <Grid2 container justifyContent='space-between' alignItems='center' columnSpacing={1}>
            <Grid2 size={0.5}>
                <HelpParameter image={cmsTitleHelp} />
            </Grid2>
            <Grid2 size='grow'><Typography variant='normalFormTitle'>{i18n.articleLogoTitleCms}</Typography></Grid2>
            <Grid2 size={3.25}>
                <SuperMultiAutocomplete
                    options={cmsEvents}
                    values={cmsTitle}
                    onChange={setCmsTitle}
                    keyLabel='title'
                />
            </Grid2>
            <Grid2 size={3.25}>
                <SuperMultiAutocomplete
                    options={cmsEvents}
                    values={moduleCmsTitle}
                    onChange={setModuleCmsTitle}
                    keyLabel='title'
                />
            </Grid2>
            <Grid2 size={0.5}>
                <SeeCmsIconTooltip />
            </Grid2>
        </Grid2>
    )
}

CmsTitle.propTypes = {
    parameters: PropTypes.arrayOf(PropTypes.shape({})),
    setParameters: PropTypes.func,
    getGlobalParameter: PropTypes.func,
    moduleParameters: PropTypes.arrayOf(PropTypes.shape({})),
    setModuleParameters: PropTypes.func,
    getGlobalModuleParameter: PropTypes.func,
    cmsEvents: PropTypes.arrayOf(PropTypes.shape({})),
}

const CmsHome = ({
    parameters = [],
    setParameters = () => {},
    getGlobalParameter = () => {},
    moduleParameters = [],
    setModuleParameters = [],
    getGlobalModuleParameter = () => {},
    cmsEvents = [],
}) => {
    const parameter = 'cmsAccueil'
    const foundParameter = parameters.find(p => p.parameter === parameter)?.value
    const foundModuleParameter = moduleParameters.find(p => p.parameter === parameter)?.value

    const [cmsHome, setCmsHome] = useState(foundParameter && parseInt(foundParameter))
    const [moduleCmsHome, setModuleCmsHome] = useState(foundModuleParameter && parseInt(foundModuleParameter))

    useDebounce(() => {
        setParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalParameter(parameter, cmsHome),
        ])
    }, 200, [cmsHome])

    useDebounce(() => {
        setModuleParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalModuleParameter(parameter, moduleCmsHome),
        ])
    }, 200, [moduleCmsHome])

    return (
        <Grid2 container justifyContent='space-between' alignItems='center' columnSpacing={1}>
            <Grid2 size={0.5}>
                <HelpParameter image={cmsHomeHelp} />
            </Grid2>
            <Grid2 size='grow'><Typography variant='normalFormTitle'>{i18n.articleObservatoryPanelCms}</Typography></Grid2>
            <Grid2 size={3.25}>
                <SuperMultiAutocomplete
                    options={cmsEvents}
                    values={cmsHome}
                    onChange={setCmsHome}
                    keyLabel='title'
                />
            </Grid2>
            <Grid2 size={3.25}>
                <SuperMultiAutocomplete
                    options={cmsEvents}
                    values={moduleCmsHome}
                    onChange={setModuleCmsHome}
                    keyLabel='title'
                />
            </Grid2>
            <Grid2 size={0.5}>
                <SeeCmsIconTooltip />
            </Grid2>
        </Grid2>
    )
}

CmsHome.propTypes = {
    parameters: PropTypes.arrayOf(PropTypes.shape({})),
    setParameters: PropTypes.func,
    getGlobalParameter: PropTypes.func,
    moduleParameters: PropTypes.arrayOf(PropTypes.shape({})),
    setModuleParameters: PropTypes.func,
    getGlobalModuleParameter: PropTypes.func,
    cmsEvents: PropTypes.arrayOf(PropTypes.shape({})),
}

const CmsInfos = ({
    parameters = [],
    setParameters = () => {},
    getGlobalParameter = () => {},
    moduleParameters = [],
    setModuleParameters = [],
    getGlobalModuleParameter = () => {},
    cmsEvents = [],
}) => {
    const parameter = 'cmsInformations'
    const foundParameter = parameters.find(p => p.parameter === parameter)?.value
    const foundModuleParameter = moduleParameters.find(p => p.parameter === parameter)?.value

    const [cmsInfos, setCmsInfos] = useState(foundParameter && parseInt(foundParameter))
    const [moduleCmsInfos, setModuleCmsInfos] = useState(foundModuleParameter && parseInt(foundModuleParameter))

    useDebounce(() => {
        setParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalParameter(parameter, cmsInfos),
        ])
    }, 200, [cmsInfos])

    useDebounce(() => {
        setModuleParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalModuleParameter(parameter, moduleCmsInfos),
        ])
    }, 200, [moduleCmsInfos])

    return (
        <Grid2 container justifyContent='space-between' alignItems='center' columnSpacing={1}>
            <Grid2 size={0.5}>
                <HelpParameter image={cmsInfosHelp} />
            </Grid2>
            <Grid2 size='grow'><Typography variant='normalFormTitle'>{i18n.articleInformationPanelCms}</Typography></Grid2>
            <Grid2 size={3.25}>
                <SuperMultiAutocomplete
                    options={cmsEvents}
                    values={cmsInfos}
                    onChange={setCmsInfos}
                    keyLabel='title'
                />
            </Grid2>
            <Grid2 size={3.25}>
                <SuperMultiAutocomplete
                    options={cmsEvents}
                    values={moduleCmsInfos}
                    onChange={setModuleCmsInfos}
                    keyLabel='title'
                />
            </Grid2>
            <Grid2 size={0.5}>
                <SeeCmsIconTooltip />
            </Grid2>
        </Grid2>
    )
}

CmsInfos.propTypes = {
    parameters: PropTypes.arrayOf(PropTypes.shape({})),
    setParameters: PropTypes.func,
    getGlobalParameter: PropTypes.func,
    moduleParameters: PropTypes.arrayOf(PropTypes.shape({})),
    setModuleParameters: PropTypes.func,
    getGlobalModuleParameter: PropTypes.func,
    cmsEvents: PropTypes.arrayOf(PropTypes.shape({})),
}

const CmsPartners = ({
    parameters = [],
    setParameters = () => {},
    getGlobalParameter = () => {},
    moduleParameters = [],
    setModuleParameters = [],
    getGlobalModuleParameter = () => {},
}) => {
    const {
        cmsCategories,
    } = useSelector(store => ({
        cmsCategories: store.EventsReducer.cmsCategories,
    }), shallowEqual)

    const parameter = 'categorieCmsPartenaires'
    const foundParameter = parameters.find(p => p.parameter === parameter)?.value
    const foundModuleParameter = moduleParameters.find(p => p.parameter === parameter)?.value

    const [cmsPartners, setCmsPartners] = useState(foundParameter && parseInt(foundParameter))
    const [moduleCmsPartners, setModuleCmsPartners] = useState(foundModuleParameter && parseInt(foundModuleParameter))

    useDebounce(() => {
        setParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalParameter(parameter, cmsPartners),
        ])
    }, 200, [cmsPartners])

    useDebounce(() => {
        setModuleParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalModuleParameter(parameter, moduleCmsPartners),
        ])
    }, 200, [moduleCmsPartners])

    const formattedCategories = useMemo(() => cmsCategories.map(cc => omit(cc, 'icon')), [cmsCategories])

    return (
        <Grid2 container justifyContent='space-between' alignItems='center' columnSpacing={1}>
            <Grid2 size={0.5}>
                <HelpParameter image={cmsPartnersHelp} />
            </Grid2>
            <Grid2 size='grow'><Typography variant='normalFormTitle'>{i18n.articleCategoryPartnersLogos}</Typography></Grid2>
            <Grid2 size={3.25}>
                <SuperMultiAutocomplete
                    options={formattedCategories}
                    values={cmsPartners}
                    onChange={setCmsPartners}
                    keyLabel='title'
                />
            </Grid2>
            <Grid2 size={3.25}>
                <SuperMultiAutocomplete
                    options={formattedCategories}
                    values={moduleCmsPartners}
                    onChange={setModuleCmsPartners}
                    keyLabel='title'
                />
            </Grid2>
            <Grid2 size={0.5}>
                <SeeCmsCategIconTooltip />
            </Grid2>
        </Grid2>
    )
}

CmsPartners.propTypes = {
    parameters: PropTypes.arrayOf(PropTypes.shape({})),
    setParameters: PropTypes.func,
    getGlobalParameter: PropTypes.func,
    moduleParameters: PropTypes.arrayOf(PropTypes.shape({})),
    setModuleParameters: PropTypes.func,
    getGlobalModuleParameter: PropTypes.func,
}

const CmsCGU = ({
    parameters = [],
    setParameters = () => {},
    getGlobalParameter = () => {},
    moduleParameters = [],
    setModuleParameters = [],
    getGlobalModuleParameter = () => {},
}) => {
    const {
        cmsCategories,
    } = useSelector(store => ({
        cmsCategories: store.EventsReducer.cmsCategories,
    }), shallowEqual)

    const parameter = 'categorieCmsCGU'
    const foundParameter = parameters.find(p => p.parameter === parameter)?.value
    const foundModuleParameter = moduleParameters.find(p => p.parameter === parameter)?.value

    const [cmsPartners, setCmsPartners] = useState(foundParameter && parseInt(foundParameter))
    const [moduleCmsPartners, setModuleCmsPartners] = useState(foundModuleParameter && parseInt(foundModuleParameter))

    useDebounce(() => {
        setParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalParameter(parameter, cmsPartners),
        ])
    }, 200, [cmsPartners])

    useDebounce(() => {
        setModuleParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalModuleParameter(parameter, moduleCmsPartners),
        ])
    }, 200, [moduleCmsPartners])

    const formattedCategories = useMemo(() => cmsCategories.map(cc => omit(cc, 'icon')), [cmsCategories])

    return (
        <Grid2 container justifyContent='space-between' alignItems='flex-start' columnSpacing={1}>
            <Grid2 size={0.5}>
                <HelpParameter image={categorieCmsCGUHelp} />
            </Grid2>
            <Grid2 container size='grow'>
                <Typography variant='normalFormTitle'>{i18n.articleCategoryCGU}</Typography>
                <Typography variant='italicSubtitle'>
                    {i18n.articleCategoryCGUDescription}
                    <Tooltip title={i18n.seeDefaultCGU} placement='right'>
                        <IconButton onClick={() => window.open('https://aquasys.fr/conditions-generales-dutilisation/', '_blank').focus()}>
                            <ArrowOutwardOutlinedIcon sx={{ fontSize: '1rem' }} />
                        </IconButton>
                    </Tooltip>
                </Typography>
            </Grid2>
            <Grid2 size={3.25}>
                <SuperMultiAutocomplete
                    options={formattedCategories}
                    values={cmsPartners}
                    onChange={setCmsPartners}
                    keyLabel='title'
                />
            </Grid2>
            <Grid2 size={3.25}>
                <SuperMultiAutocomplete
                    options={formattedCategories}
                    values={moduleCmsPartners}
                    onChange={setModuleCmsPartners}
                    keyLabel='title'
                />
            </Grid2>
            <Grid2 size={0.5}>
                <SeeCmsCategIconTooltip />
            </Grid2>
        </Grid2>
    )
}

CmsCGU.propTypes = {
    parameters: PropTypes.arrayOf(PropTypes.shape({})),
    setParameters: PropTypes.func,
    getGlobalParameter: PropTypes.func,
    moduleParameters: PropTypes.arrayOf(PropTypes.shape({})),
    setModuleParameters: PropTypes.func,
    getGlobalModuleParameter: PropTypes.func,
}

const PasswordPolicy = ({
    parameters = [],
    setParameters = () => {},
    getGlobalParameter = () => {},
    moduleParameters = [],
    setModuleParameters = [],
    getGlobalModuleParameter = () => {},
}) => {
    const parameter = 'passwordPolicy'
    const foundParameter = parameters.find(p => p.parameter === parameter)?.value
    const foundModuleParameter = moduleParameters.find(p => p.parameter === parameter)?.value

    const [passwordPolicy, setPasswordPolicy] = useState(foundParameter)
    const [modulePasswordPolicy, setModulePasswordPolicy] = useState(foundModuleParameter)

    useDebounce(() => {
        setParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalParameter(parameter, passwordPolicy),
        ])
    }, 200, [passwordPolicy])

    useDebounce(() => {
        setModuleParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalModuleParameter(parameter, modulePasswordPolicy),
        ])
    }, 200, [modulePasswordPolicy])

    return (
        <Grid2 container justifyContent='space-between' alignItems='center' columnSpacing={1}>
            <Grid2 size={0.5}>
                <HelpParameter image={passwordPolicyHelp} />
            </Grid2>
            <Grid2 container size='grow'>
                <Typography variant='normalFormTitle'>{i18n.passwordRegex}</Typography>
            </Grid2>
            <Grid2 size={3.25}>
                <SimpleInput
                    value={passwordPolicy}
                    onChange={setPasswordPolicy}
                    placeholder='(?=(.*?[A-Z]){0,})(?=(.*?[0-9]){0,})(?=(.*?[^\w\s]){0,}).{6,}$'
                />
            </Grid2>
            <Grid2 size={3.25}>
                <SimpleInput
                    value={modulePasswordPolicy}
                    onChange={setModulePasswordPolicy}
                    placeholder='(?=(.*?[A-Z]){0,})(?=(.*?[0-9]){0,})(?=(.*?[^\w\s]){0,}).{6,}$'
                />
            </Grid2>
            <Grid2 size={0.5} />
        </Grid2>
    )
}

PasswordPolicy.propTypes = {
    parameters: PropTypes.arrayOf(PropTypes.shape({})),
    setParameters: PropTypes.func,
    getGlobalParameter: PropTypes.func,
    moduleParameters: PropTypes.arrayOf(PropTypes.shape({})),
    setModuleParameters: PropTypes.func,
    getGlobalModuleParameter: PropTypes.func,
}

const SecurityPasswordDescription = ({
    parameters = [],
    setParameters = () => {},
    getGlobalParameter = () => {},
    moduleParameters = [],
    setModuleParameters = [],
    getGlobalModuleParameter = () => {},
}) => {
    const parameter = 'securityPasswordDescription'
    const foundParameter = parameters.find(p => p.parameter === parameter)?.value
    const foundModuleParameter = moduleParameters.find(p => p.parameter === parameter)?.value

    const [securityPasswordDescription, setSecurityPasswordDescription] = useState(foundParameter)
    const [moduleSecurityPasswordDescription, setModuleSecurityPasswordDescription] = useState(foundModuleParameter)

    useDebounce(() => {
        setParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalParameter(parameter, securityPasswordDescription),
        ])
    }, 200, [securityPasswordDescription])

    useDebounce(() => {
        setModuleParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalModuleParameter(parameter, moduleSecurityPasswordDescription),
        ])
    }, 200, [moduleSecurityPasswordDescription])

    return (
        <Grid2 container justifyContent='space-between' alignItems='center' columnSpacing={1}>
            <Grid2 size={0.5}>
                <HelpParameter image={securityPasswordDescriptionHelp} />
            </Grid2>
            <Grid2 container size='grow'>
                <Typography variant='normalFormTitle'>{i18n.passwordFormattingRules}</Typography>
            </Grid2>
            <Grid2 size={3.25}>
                <SimpleInput
                    value={securityPasswordDescription}
                    onChange={setSecurityPasswordDescription}
                    placeholder={`${i18n.pleaseEnterAtLeastOneCapitalLetter}, ...`}
                />
            </Grid2>
            <Grid2 size={3.25}>
                <SimpleInput
                    value={moduleSecurityPasswordDescription}
                    onChange={setModuleSecurityPasswordDescription}
                    placeholder={`${i18n.pleaseEnterAtLeastOneCapitalLetter}, ...`}
                />
            </Grid2>
            <Grid2 size={0.5} />
        </Grid2>
    )
}

SecurityPasswordDescription.propTypes = {
    parameters: PropTypes.arrayOf(PropTypes.shape({})),
    setParameters: PropTypes.func,
    getGlobalParameter: PropTypes.func,
    moduleParameters: PropTypes.arrayOf(PropTypes.shape({})),
    setModuleParameters: PropTypes.func,
    getGlobalModuleParameter: PropTypes.func,
}

const CmsActualities = ({
    parameters = [],
    setParameters = () => {},
    getGlobalParameter = () => {},
    moduleParameters = [],
    setModuleParameters = [],
    getGlobalModuleParameter = () => {},
}) => {
    const {
        cmsCategories,
    } = useSelector(store => ({
        cmsCategories: store.EventsReducer.cmsCategories,
    }), shallowEqual)

    const parameter = 'categoriesCmsActualites'
    const foundParameter = parameters.find(p => p.parameter === parameter)?.value
    const foundModuleParameter = moduleParameters.find(p => p.parameter === parameter)?.value

    const [categoriesCmsActualites, setCategoriesCmsActualites] = useState(foundParameter && parseInt(foundParameter))
    const [moduleCategoriesCmsActualites, setModuleCategoriesCmsActualites] = useState(foundModuleParameter && parseInt(foundModuleParameter))

    useDebounce(() => {
        setParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalParameter(parameter, categoriesCmsActualites),
        ])
    }, 200, [categoriesCmsActualites])

    useDebounce(() => {
        setModuleParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalModuleParameter(parameter, moduleCategoriesCmsActualites),
        ])
    }, 200, [moduleCategoriesCmsActualites])

    const formattedCategories = useMemo(() => cmsCategories.map(cc => omit(cc, 'icon')), [cmsCategories])

    return (
        <Grid2 container justifyContent='space-between' alignItems='center' columnSpacing={1}>
            <Grid2 size={0.5}>
                <HelpParameter image={categoriesCmsActualitesHelp} />
            </Grid2>
            <Grid2 container size='grow'>
                <Typography variant='normalFormTitle'>{i18n.newArticleCategory}</Typography>
            </Grid2>
            <Grid2 size={3.25}>
                <SuperMultiAutocomplete
                    options={formattedCategories}
                    values={categoriesCmsActualites}
                    onChange={setCategoriesCmsActualites}
                    keyLabel='title'
                />
            </Grid2>
            <Grid2 size={3.25}>
                <SuperMultiAutocomplete
                    options={formattedCategories}
                    values={moduleCategoriesCmsActualites}
                    onChange={setModuleCategoriesCmsActualites}
                    keyLabel='title'
                />
            </Grid2>
            <Grid2 size={0.5}>
                <SeeCmsCategIconTooltip />
            </Grid2>
        </Grid2>
    )
}

CmsActualities.propTypes = {
    parameters: PropTypes.arrayOf(PropTypes.shape({})),
    setParameters: PropTypes.func,
    getGlobalParameter: PropTypes.func,
    moduleParameters: PropTypes.arrayOf(PropTypes.shape({})),
    setModuleParameters: PropTypes.func,
    getGlobalModuleParameter: PropTypes.func,
}

const CmsDocuments = ({
    parameters = [],
    setParameters = () => {},
    getGlobalParameter = () => {},
    moduleParameters = [],
    setModuleParameters = [],
    getGlobalModuleParameter = () => {},
}) => {
    const {
        cmsCategories,
    } = useSelector(store => ({
        cmsCategories: store.EventsReducer.cmsCategories,
    }), shallowEqual)

    const parameter = 'categoriesCmsDocuments'
    const foundParameter = parameters.find(p => p.parameter === parameter)?.value
    const foundModuleParameter = moduleParameters.find(p => p.parameter === parameter)?.value

    const [categoriesCmsDocuments, setCategoriesCmsDocuments] = useState(foundParameter && foundParameter.split(',').map((id) => Number(id)))
    const [moduleCategoriesCmsDocuments, setModuleCategoriesCmsDocuments] = useState(foundModuleParameter && foundModuleParameter.split(',').map((id) => Number(id)))

    useDebounce(() => {
        setParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalParameter(parameter, categoriesCmsDocuments.join(',')),
        ])
    }, 200, [categoriesCmsDocuments])

    useDebounce(() => {
        setModuleParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalModuleParameter(parameter, moduleCategoriesCmsDocuments.join(',')),
        ])
    }, 200, [moduleCategoriesCmsDocuments])

    const formattedCategories = useMemo(() => cmsCategories.map(cc => omit(cc, 'icon')), [cmsCategories])

    return (
        <Grid2 container justifyContent='space-between' alignItems='center' columnSpacing={1}>
            <Grid2 size={0.5}>
                <HelpParameter image={categoriesCmsDocumentsHelp} />
            </Grid2>
            <Grid2 container size='grow'>
                <Typography variant='normalFormTitle'>{i18n.documentsArticleCategory}</Typography>
            </Grid2>
            <Grid2 size={3.25}>
                <SuperMultiAutocomplete
                    options={formattedCategories}
                    values={categoriesCmsDocuments}
                    onChange={setCategoriesCmsDocuments}
                    keyLabel='title'
                    multiple
                    limit={1}
                />
            </Grid2>
            <Grid2 size={3.25}>
                <SuperMultiAutocomplete
                    options={formattedCategories}
                    values={moduleCategoriesCmsDocuments}
                    onChange={setModuleCategoriesCmsDocuments}
                    keyLabel='title'
                    multiple
                    limit={1}
                />
            </Grid2>
            <Grid2 size={0.5}>
                <SeeCmsCategIconTooltip />
            </Grid2>
        </Grid2>
    )
}

CmsDocuments.propTypes = {
    parameters: PropTypes.arrayOf(PropTypes.shape({})),
    setParameters: PropTypes.func,
    getGlobalParameter: PropTypes.func,
    moduleParameters: PropTypes.arrayOf(PropTypes.shape({})),
    setModuleParameters: PropTypes.func,
    getGlobalModuleParameter: PropTypes.func,
}

const SupportContactSchedule = ({
    parameters = [],
    setParameters = () => {},
    getGlobalParameter = () => {},
    moduleParameters = [],
    setModuleParameters = [],
    getGlobalModuleParameter = () => {},
}) => {
    const parameter = 'supportContactSchedule'
    const foundParameter = parameters.find(p => p.parameter === parameter)?.value
    const foundModuleParameter = moduleParameters.find(p => p.parameter === parameter)?.value

    const [supportContactSchedule, setSupportContactSchedule] = useState(foundParameter)
    const [moduleSupportContactSchedule, setModuleSupportContactSchedule] = useState(foundModuleParameter)

    useDebounce(() => {
        setParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalParameter(parameter, supportContactSchedule),
        ])
    }, 200, [supportContactSchedule])

    useDebounce(() => {
        setModuleParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalModuleParameter(parameter, moduleSupportContactSchedule),
        ])
    }, 200, [moduleSupportContactSchedule])

    return (
        <Grid2 container justifyContent='space-between' alignItems='center' columnSpacing={1}>
            <Grid2 size={0.5}>
                <HelpParameter image={supportContactScheduleHelp} />
            </Grid2>
            <Grid2 container size='grow'>
                <Typography variant='normalFormTitle'>{i18n.supportOpeningDays}</Typography>
            </Grid2>
            <Grid2 size={3.25}>
                <SimpleInput
                    value={supportContactSchedule}
                    onChange={setSupportContactSchedule}
                    placeholder={i18n.mondayThroughFriday}
                />
            </Grid2>
            <Grid2 size={3.25}>
                <SimpleInput
                    value={moduleSupportContactSchedule}
                    onChange={setModuleSupportContactSchedule}
                    placeholder={i18n.mondayThroughFriday}
                />
            </Grid2>
            <Grid2 size={0.5} />
        </Grid2>
    )
}

SupportContactSchedule.propTypes = {
    parameters: PropTypes.arrayOf(PropTypes.shape({})),
    setParameters: PropTypes.func,
    getGlobalParameter: PropTypes.func,
    moduleParameters: PropTypes.arrayOf(PropTypes.shape({})),
    setModuleParameters: PropTypes.func,
    getGlobalModuleParameter: PropTypes.func,
}

const SupportPhoneNumber = ({
    parameters = [],
    setParameters = () => {},
    getGlobalParameter = () => {},
    moduleParameters = [],
    setModuleParameters = [],
    getGlobalModuleParameter = () => {},
}) => {
    const parameter = 'supportPhoneNumber'
    const foundParameter = parameters.find(p => p.parameter === parameter)?.value
    const foundModuleParameter = moduleParameters.find(p => p.parameter === parameter)?.value

    const [supportPhoneNumber, setSupportPhoneNumber] = useState(foundParameter)
    const [moduleSupportPhoneNumber, setModuleSupportPhoneNumber] = useState(foundModuleParameter)

    useDebounce(() => {
        setParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalParameter(parameter, supportPhoneNumber),
        ])
    }, 200, [supportPhoneNumber])

    useDebounce(() => {
        setModuleParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalModuleParameter(parameter, moduleSupportPhoneNumber),
        ])
    }, 200, [moduleSupportPhoneNumber])

    return (
        <Grid2 container justifyContent='space-between' alignItems='center' columnSpacing={1}>
            <Grid2 size={0.5}>
                <HelpParameter image={supportPhoneNumberHelp} />
            </Grid2>
            <Grid2 container size='grow'>
                <Typography variant='normalFormTitle'>{i18n.supportPhoneNumner}</Typography>
            </Grid2>
            <Grid2 size={3.25}>
                <SimpleInput
                    value={supportPhoneNumber}
                    onChange={setSupportPhoneNumber}
                    placeholder='09 51 72 41 72'
                />
            </Grid2>
            <Grid2 size={3.25}>
                <SimpleInput
                    value={moduleSupportPhoneNumber}
                    onChange={setModuleSupportPhoneNumber}
                    placeholder='09 51 72 41 72'
                />
            </Grid2>
            <Grid2 size={0.5} />
        </Grid2>
    )
}

SupportPhoneNumber.propTypes = {
    parameters: PropTypes.arrayOf(PropTypes.shape({})),
    setParameters: PropTypes.func,
    getGlobalParameter: PropTypes.func,
    moduleParameters: PropTypes.arrayOf(PropTypes.shape({})),
    setModuleParameters: PropTypes.func,
    getGlobalModuleParameter: PropTypes.func,
}

const SupportMailAddress = ({
    parameters = [],
    setParameters = () => {},
    getGlobalParameter = () => {},
    moduleParameters = [],
    setModuleParameters = [],
    getGlobalModuleParameter = () => {},
}) => {
    const parameter = 'supportMailAddress'
    const foundParameter = parameters.find(p => p.parameter === parameter)?.value
    const foundModuleParameter = moduleParameters.find(p => p.parameter === parameter)?.value

    const [supportMailAddress, setSupportMailAddress] = useState(foundParameter)
    const [moduleSupportMailAddress, setModuleSupportMailAddress] = useState(foundModuleParameter)

    useDebounce(() => {
        setParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalParameter(parameter, supportMailAddress),
        ])
    }, 200, [supportMailAddress])

    useDebounce(() => {
        setModuleParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalModuleParameter(parameter, moduleSupportMailAddress),
        ])
    }, 200, [moduleSupportMailAddress])

    return (
        <Grid2 container justifyContent='space-between' alignItems='center' columnSpacing={1}>
            <Grid2 size={0.5}>
                <HelpParameter image={supportMailAddressHelp} />
            </Grid2>
            <Grid2 container size='grow'>
                <Typography variant='normalFormTitle'>{i18n.supportEMailAddress}</Typography>
            </Grid2>
            <Grid2 size={3.25}>
                <SimpleInput
                    value={supportMailAddress}
                    onChange={setSupportMailAddress}
                    placeholder='support@aquasys.fr'
                />
            </Grid2>
            <Grid2 size={3.25}>
                <SimpleInput
                    value={moduleSupportMailAddress}
                    onChange={setModuleSupportMailAddress}
                    placeholder='support@aquasys.fr'
                />
            </Grid2>
            <Grid2 size={0.5} />
        </Grid2>
    )
}

SupportMailAddress.propTypes = {
    parameters: PropTypes.arrayOf(PropTypes.shape({})),
    setParameters: PropTypes.func,
    getGlobalParameter: PropTypes.func,
    moduleParameters: PropTypes.arrayOf(PropTypes.shape({})),
    setModuleParameters: PropTypes.func,
    getGlobalModuleParameter: PropTypes.func,
}

const CmsSupport = ({
    parameters = [],
    setParameters = () => {},
    getGlobalParameter = () => {},
    moduleParameters = [],
    setModuleParameters = [],
    getGlobalModuleParameter = () => {},
    cmsEvents = [],
}) => {
    const parameter = 'cmsSupport'
    const foundParameter = parameters.find(p => p.parameter === parameter)?.value
    const foundModuleParameter = moduleParameters.find(p => p.parameter === parameter)?.value

    const [cmsTitle, setCmsTitle] = useState(foundParameter && parseInt(foundParameter))
    const [moduleCmsTitle, setModuleCmsTitle] = useState(foundModuleParameter && parseInt(foundModuleParameter))

    useDebounce(() => {
        setParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalParameter(parameter, cmsTitle),
        ])
    }, 200, [cmsTitle])

    useDebounce(() => {
        setModuleParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalModuleParameter(parameter, moduleCmsTitle),
        ])
    }, 200, [moduleCmsTitle])

    return (
        <Grid2 container justifyContent='space-between' alignItems='center' columnSpacing={1}>
            <Grid2 size={0.5}>
                <HelpParameter image={cmsSupportHelp} />
            </Grid2>
            <Grid2 size='grow'><Typography variant='normalFormTitle'>{i18n.articleSubtitleSupportPanelCMS}</Typography></Grid2>
            <Grid2 size={3.25}>
                <SuperMultiAutocomplete
                    options={cmsEvents}
                    values={cmsTitle}
                    onChange={setCmsTitle}
                    keyLabel='title'
                />
            </Grid2>
            <Grid2 size={3.25}>
                <SuperMultiAutocomplete
                    options={cmsEvents}
                    values={moduleCmsTitle}
                    onChange={setModuleCmsTitle}
                    keyLabel='title'
                />
            </Grid2>
            <Grid2 size={0.5}>
                <SeeCmsIconTooltip />
            </Grid2>
        </Grid2>
    )
}

CmsSupport.propTypes = {
    parameters: PropTypes.arrayOf(PropTypes.shape({})),
    setParameters: PropTypes.func,
    getGlobalParameter: PropTypes.func,
    moduleParameters: PropTypes.arrayOf(PropTypes.shape({})),
    setModuleParameters: PropTypes.func,
    getGlobalModuleParameter: PropTypes.func,
    cmsEvents: PropTypes.arrayOf(PropTypes.shape({})),
}

const CmsForum = ({
    parameters = [],
    setParameters = () => {},
    getGlobalParameter = () => {},
    moduleParameters = [],
    setModuleParameters = [],
    getGlobalModuleParameter = () => {},
}) => {
    const {
        cmsCategories,
    } = useSelector(store => ({
        cmsCategories: store.EventsReducer.cmsCategories,
    }), shallowEqual)

    const parameter = 'categoriesCmsFaq'
    const foundParameter = parameters.find(p => p.parameter === parameter)?.value
    const foundModuleParameter = moduleParameters.find(p => p.parameter === parameter)?.value

    const [categoriesCmsForum, setCategoriesCmsForum] = useState(foundParameter && parseInt(foundParameter))
    const [moduleCategoriesCmsForum, setModuleCategoriesCmsForum] = useState(foundModuleParameter && parseInt(foundModuleParameter))

    useDebounce(() => {
        setParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalParameter(parameter, categoriesCmsForum),
        ])
    }, 200, [categoriesCmsForum])

    useDebounce(() => {
        setModuleParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalModuleParameter(parameter, moduleCategoriesCmsForum),
        ])
    }, 200, [moduleCategoriesCmsForum])

    const formattedCategories = useMemo(() => cmsCategories.map(cc => omit(cc, 'icon')), [cmsCategories])

    return (
        <Grid2 container justifyContent='space-between' alignItems='center' columnSpacing={1}>
            <Grid2 size={0.5}>
                <HelpParameter image={categoriesCmsFaqHelp} />
            </Grid2>
            <Grid2 container size='grow'>
                <Typography variant='normalFormTitle'>{i18n.forumArticleCategory}</Typography>
            </Grid2>
            <Grid2 size={3.25}>
                <SuperMultiAutocomplete
                    options={formattedCategories}
                    values={categoriesCmsForum}
                    onChange={setCategoriesCmsForum}
                    keyLabel='title'
                />
            </Grid2>
            <Grid2 size={3.25}>
                <SuperMultiAutocomplete
                    options={formattedCategories}
                    values={moduleCategoriesCmsForum}
                    onChange={setModuleCategoriesCmsForum}
                    keyLabel='title'
                />
            </Grid2>
            <Grid2 size={0.5}>
                <SeeCmsCategIconTooltip />
            </Grid2>
        </Grid2>
    )
}

CmsForum.propTypes = {
    parameters: PropTypes.arrayOf(PropTypes.shape({})),
    setParameters: PropTypes.func,
    getGlobalParameter: PropTypes.func,
    moduleParameters: PropTypes.arrayOf(PropTypes.shape({})),
    setModuleParameters: PropTypes.func,
    getGlobalModuleParameter: PropTypes.func,
}

const CmsReponsesForum = ({
    parameters = [],
    setParameters = () => {},
    getGlobalParameter = () => {},
    moduleParameters = [],
    setModuleParameters = [],
    getGlobalModuleParameter = () => {},
}) => {
    const parameter = 'cmsReponses'
    const foundParameter = parameters.find(p => p.parameter === parameter)?.value
    const foundModuleParameter = moduleParameters.find(p => p.parameter === parameter)?.value

    const [cmsReponses, setCmsReponses] = useState(foundParameter === 'true')
    const [moduleCmsReponses, setModuleCmsReponses] = useState(foundModuleParameter === 'true')

    useDebounce(() => {
        setParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalParameter(parameter, `${cmsReponses}`),
        ])
    }, 200, [cmsReponses])

    useDebounce(() => {
        setModuleParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalModuleParameter(parameter, `${moduleCmsReponses}`),
        ])
    }, 200, [moduleCmsReponses])

    return (
        <Grid2 container justifyContent='space-between' alignItems='center' columnSpacing={1}>
            <Grid2 size={0.5}>
                <HelpParameter image={cmsReponsesHelp} />
            </Grid2>
            <Grid2 container size='grow'>
                <Typography variant='normalFormTitle'>{i18n.abilityAddTopicsToForum}</Typography>
            </Grid2>
            <Grid2 container size={3.25}>
                <Box onClick={() => setCmsReponses(prev => !prev)}>
                    <Switch checked={cmsReponses} />
                </Box>
            </Grid2>
            <Grid2 container size={3.25}>
                <Box onClick={() => setModuleCmsReponses(prev => !prev)}>
                    <Switch checked={moduleCmsReponses} />
                </Box>
            </Grid2>
            <Grid2 size={0.5} />
        </Grid2>
    )
}

CmsReponsesForum.propTypes = {
    parameters: PropTypes.arrayOf(PropTypes.shape({})),
    setParameters: PropTypes.func,
    getGlobalParameter: PropTypes.func,
    moduleParameters: PropTypes.arrayOf(PropTypes.shape({})),
    setModuleParameters: PropTypes.func,
    getGlobalModuleParameter: PropTypes.func,
}

const IdDailyMin = ({
    parameters = [],
    setParameters = () => {},
    getGlobalParameter = () => {},
    moduleParameters = [],
    setModuleParameters = [],
    getGlobalModuleParameter = () => {},
}) => {
    const {
        piezoDataTypes,
    } = useSelector(store => ({
        piezoDataTypes: store.StationReducer.piezoDataTypes,
    }), shallowEqual)

    const parameter = 'ID_MIN_JOURNALIER'
    const foundParameter = parameters.find(p => p.parameter === parameter)?.value
    const foundModuleParameter = moduleParameters.find(p => p.parameter === parameter)?.value

    const [idDailyMin, setIdDailyMin] = useState(foundParameter && parseInt(foundParameter))
    const [moduleIdDailyMin, setModuleIdDailyMin] = useState(foundModuleParameter && parseInt(foundModuleParameter))

    useDebounce(() => {
        setParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalParameter(parameter, idDailyMin),
        ])
    }, 200, [idDailyMin])

    useDebounce(() => {
        setModuleParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalModuleParameter(parameter, moduleIdDailyMin),
        ])
    }, 200, [moduleIdDailyMin])

    const formattedPiezoDataTypes = useMemo(() => piezoDataTypes.map(pdt => ({ ...pdt, displayedName: `${pdt.id} - ${pdt.name}` })), [piezoDataTypes])

    return (
        <Grid2 container justifyContent='space-between' alignItems='center' columnSpacing={1}>
            <Grid2 size={0.5}>
                <HelpParameter image={idDailyMinHelp} />
            </Grid2>
            <Grid2 size='grow'><Typography variant='normalFormTitle'>{i18n.dailyMinId}</Typography></Grid2>
            <Grid2 size={3.25}>
                <SuperMultiAutocomplete
                    options={formattedPiezoDataTypes}
                    values={idDailyMin}
                    onChange={setIdDailyMin}
                    keyLabel='displayedName'
                />
            </Grid2>
            <Grid2 size={3.25}>
                <SuperMultiAutocomplete
                    options={formattedPiezoDataTypes}
                    values={moduleIdDailyMin}
                    onChange={setModuleIdDailyMin}
                    keyLabel='displayedName'
                />
            </Grid2>
            <Grid2 size={0.5}>
                <SeeOtherIconTooltip
                    title={i18n.seeDataTypes}
                    link={`${usedUrl}#/piezometry/piezometryOptions`}
                />
            </Grid2>
        </Grid2>
    )
}

IdDailyMin.propTypes = {
    parameters: PropTypes.arrayOf(PropTypes.shape({})),
    setParameters: PropTypes.func,
    getGlobalParameter: PropTypes.func,
    moduleParameters: PropTypes.arrayOf(PropTypes.shape({})),
    setModuleParameters: PropTypes.func,
    getGlobalModuleParameter: PropTypes.func,
}

const WithStats = ({
    parameters = [],
    setParameters = () => {},
    getGlobalParameter = () => {},
    moduleParameters = [],
    setModuleParameters = [],
    getGlobalModuleParameter = () => {},
    isWidget = false,
}) => {
    const parameter = isWidget ? 'withStatsWidget' : 'withStats'
    const foundParameter = parameters.find(p => p.parameter === parameter)?.value
    const foundModuleParameter = moduleParameters.find(p => p.parameter === parameter)?.value

    const [withStats, setWithStats] = useState(foundParameter === 'true')
    const [moduleWithStats, setModuleWithStats] = useState(foundModuleParameter === 'true')

    useDebounce(() => {
        setParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalParameter(parameter, `${withStats}`),
        ])
    }, 200, [withStats])

    useDebounce(() => {
        setModuleParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalModuleParameter(parameter, `${moduleWithStats}`),
        ])
    }, 200, [moduleWithStats])

    return (
        <Grid2 container justifyContent='space-between' alignItems='flex-start' columnSpacing={1}>
            <Grid2 size={0.5}>
                <HelpParameter image={withStatsHelp} />
            </Grid2>
            <Grid2 container size='grow'>
                <Typography variant='normalFormTitle'>{i18n.abilityToViewEnvelopes}</Typography>
                <Typography variant='italicSubtitle'>
                    {i18n.currentlyAvailableEverywhereExceptForPluviometry}
                </Typography>
            </Grid2>
            <Grid2 container size={3.25}>
                <Box onClick={() => setWithStats(prev => !prev)}>
                    <Switch checked={withStats} />
                </Box>
            </Grid2>
            {!!isWidget && (
                <Grid2 container size={3.25}>
                    <Box onClick={() => setModuleWithStats(prev => !prev)}>
                        <Switch checked={moduleWithStats} />
                    </Box>
                </Grid2>
            )}
            <Grid2 offset={!isWidget && 3.25} size={0.5} />
        </Grid2>
    )
}

WithStats.propTypes = {
    parameters: PropTypes.arrayOf(PropTypes.shape({})),
    setParameters: PropTypes.func,
    getGlobalParameter: PropTypes.func,
    moduleParameters: PropTypes.arrayOf(PropTypes.shape({})),
    setModuleParameters: PropTypes.func,
    getGlobalModuleParameter: PropTypes.func,
    isWidget: PropTypes.bool,
}

const PiezoValidOnly = ({
    parameters = [],
    setParameters = () => {},
    getGlobalParameter = () => {},
    moduleParameters = [],
    setModuleParameters = [],
    getGlobalModuleParameter = () => {},
    isWidget = false,
}) => {
    const parameter = isWidget ? 'piezoValidOnlyWidget' : 'piezoValidOnly'
    const foundParameter = parameters.find(p => p.parameter === parameter)?.value
    const foundModuleParameter = moduleParameters.find(p => p.parameter === parameter)?.value

    const [piezoValidOnly, setPiezoValidOnly] = useState(foundParameter === 'true')
    const [modulePiezoValidOnly, setModulePiezoValidOnly] = useState(foundModuleParameter === 'true')

    useDebounce(() => {
        setParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalParameter(parameter, `${piezoValidOnly}`),
        ])
    }, 200, [piezoValidOnly])

    useDebounce(() => {
        setModuleParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalModuleParameter(parameter, `${modulePiezoValidOnly}`),
        ])
    }, 200, [modulePiezoValidOnly])

    return (
        <Grid2 container justifyContent='space-between' alignItems='center' columnSpacing={1}>
            <Grid2 size={0.5}>
                <HelpParameter image={piezoValidOnlyHelp} />
            </Grid2>
            <Grid2 container size='grow'>
                <Typography variant='normalFormTitle'>{i18n.validatedDataOnly}</Typography>
            </Grid2>
            <Grid2 container size={3.25}>
                <Box onClick={() => setPiezoValidOnly(prev => !prev)}>
                    <Switch checked={piezoValidOnly} />
                </Box>
            </Grid2>
            <Grid2 container size={3.75}>
                <Box onClick={() => setModulePiezoValidOnly(prev => !prev)}>
                    <Switch checked={modulePiezoValidOnly} />
                </Box>
            </Grid2>
        </Grid2>
    )
}

PiezoValidOnly.propTypes = {
    parameters: PropTypes.arrayOf(PropTypes.shape({})),
    setParameters: PropTypes.func,
    getGlobalParameter: PropTypes.func,
    moduleParameters: PropTypes.arrayOf(PropTypes.shape({})),
    setModuleParameters: PropTypes.func,
    getGlobalModuleParameter: PropTypes.func,
    isWidget: PropTypes.bool,
}

const HydroValidOnly = ({
    parameters = [],
    setParameters = () => {},
    getGlobalParameter = () => {},
    moduleParameters = [],
    setModuleParameters = [],
    getGlobalModuleParameter = () => {},
    isWidget = false,
}) => {
    const parameter = isWidget ? 'hydroValidOnlyWidget' : 'hydroValidOnly'
    const foundParameter = parameters.find(p => p.parameter === parameter)?.value
    const foundModuleParameter = moduleParameters.find(p => p.parameter === parameter)?.value

    const [hydroValidOnly, setHydroValidOnly] = useState(foundParameter === 'true')
    const [moduleHydroValidOnly, setModuleHydroValidOnly] = useState(foundModuleParameter === 'true')

    useDebounce(() => {
        setParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalParameter(parameter, `${hydroValidOnly}`),
        ])
    }, 200, [hydroValidOnly])

    useDebounce(() => {
        setModuleParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalModuleParameter(parameter, `${moduleHydroValidOnly}`),
        ])
    }, 200, [moduleHydroValidOnly])

    return (
        <Grid2 container justifyContent='space-between' alignItems='center' columnSpacing={1}>
            <Grid2 size={0.5}>
                <HelpParameter image={hydroValidOnlyHelp} />
            </Grid2>
            <Grid2 container size='grow'>
                <Typography variant='normalFormTitle'>{i18n.validatedDataOnly}</Typography>
            </Grid2>
            <Grid2 container size={3.25}>
                <Box onClick={() => setHydroValidOnly(prev => !prev)}>
                    <Switch checked={hydroValidOnly} />
                </Box>
            </Grid2>
            <Grid2 container size={3.75}>
                <Box onClick={() => setModuleHydroValidOnly(prev => !prev)}>
                    <Switch checked={moduleHydroValidOnly} />
                </Box>
            </Grid2>
        </Grid2>
    )
}

HydroValidOnly.propTypes = {
    parameters: PropTypes.arrayOf(PropTypes.shape({})),
    setParameters: PropTypes.func,
    getGlobalParameter: PropTypes.func,
    moduleParameters: PropTypes.arrayOf(PropTypes.shape({})),
    setModuleParameters: PropTypes.func,
    getGlobalModuleParameter: PropTypes.func,
    isWidget: PropTypes.bool,
}

const PluvioValidOnly = ({
    parameters = [],
    setParameters = () => {},
    getGlobalParameter = () => {},
    moduleParameters = [],
    setModuleParameters = [],
    getGlobalModuleParameter = () => {},
    isWidget = false,
}) => {
    const parameter = isWidget ? 'pluvioValidOnlyWidget' : 'pluvioValidOnly'
    const foundParameter = parameters.find(p => p.parameter === parameter)?.value
    const foundModuleParameter = moduleParameters.find(p => p.parameter === parameter)?.value

    const [pluvioValidOnly, setPluvioValidOnly] = useState(foundParameter === 'true')
    const [modulePluvioValidOnly, setModulePluvioValidOnly] = useState(foundModuleParameter === 'true')

    useDebounce(() => {
        setParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalParameter(parameter, `${pluvioValidOnly}`),
        ])
    }, 200, [pluvioValidOnly])

    useDebounce(() => {
        setModuleParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalModuleParameter(parameter, `${modulePluvioValidOnly}`),
        ])
    }, 200, [modulePluvioValidOnly])

    return (
        <Grid2 container justifyContent='space-between' alignItems='center' columnSpacing={1}>
            <Grid2 size={0.5}>
                <HelpParameter image={pluvioValidOnlyHelp} />
            </Grid2>
            <Grid2 container size='grow'>
                <Typography variant='normalFormTitle'>{i18n.validatedDataOnly}</Typography>
            </Grid2>
            <Grid2 container size={3.25}>
                <Box onClick={() => setPluvioValidOnly(prev => !prev)}>
                    <Switch checked={pluvioValidOnly} />
                </Box>
            </Grid2>
            <Grid2 container size={3.75}>
                <Box onClick={() => setModulePluvioValidOnly(prev => !prev)}>
                    <Switch checked={modulePluvioValidOnly} />
                </Box>
            </Grid2>
        </Grid2>
    )
}

PluvioValidOnly.propTypes = {
    parameters: PropTypes.arrayOf(PropTypes.shape({})),
    setParameters: PropTypes.func,
    getGlobalParameter: PropTypes.func,
    moduleParameters: PropTypes.arrayOf(PropTypes.shape({})),
    setModuleParameters: PropTypes.func,
    getGlobalModuleParameter: PropTypes.func,
    isWidget: PropTypes.bool,
}

const PiezoHorizons = ({
    parameters = [],
    setParameters = () => {},
    getGlobalParameter = () => {},
    moduleParameters = [],
    setModuleParameters = [],
    getGlobalModuleParameter = () => {},
    isWidget = false,
}) => {
    const parameter = isWidget ? 'piezometryHorizonsWidget' : 'piezometryHorizons'
    const foundParameter = parameters.find(p => p.parameter === parameter)?.value
    const foundModuleParameter = moduleParameters.find(p => p.parameter === parameter)?.value

    const [piezoHorizons, setPiezoHorizons] = useState(foundParameter)
    const [modulePiezoHorizons, setModulePiezoHorizons] = useState(foundModuleParameter)

    useDebounce(() => {
        setParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalParameter(parameter, piezoHorizons),
        ])
    }, 200, [piezoHorizons])

    useDebounce(() => {
        setModuleParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalModuleParameter(parameter, modulePiezoHorizons),
        ])
    }, 200, [modulePiezoHorizons])

    return (
        <Grid2 container justifyContent='space-between' alignItems='center' columnSpacing={1}>
            <Grid2 size={0.5}>
                <HelpParameter image={piezometryHorizonsHelp} />
            </Grid2>
            <Grid2 container size='grow'>
                <Typography variant='normalFormTitle'>{i18n.chartHorizons}</Typography>
            </Grid2>
            <Grid2 size={3.25}>
                <SimpleInput
                    value={piezoHorizons}
                    onChange={setPiezoHorizons}
                    placeholder='7,30,90,365,HISTO'
                />
            </Grid2>
            <Grid2 size={3.25}>
                <SimpleInput
                    value={modulePiezoHorizons}
                    onChange={setModulePiezoHorizons}
                    placeholder='30,365,HISTO'
                />
            </Grid2>
            <Grid2 size={0.5} />
        </Grid2>
    )
}

PiezoHorizons.propTypes = {
    parameters: PropTypes.arrayOf(PropTypes.shape({})),
    setParameters: PropTypes.func,
    getGlobalParameter: PropTypes.func,
    moduleParameters: PropTypes.arrayOf(PropTypes.shape({})),
    setModuleParameters: PropTypes.func,
    getGlobalModuleParameter: PropTypes.func,
    isWidget: PropTypes.bool,
}

const HydroHorizons = ({
    parameters = [],
    setParameters = () => {},
    getGlobalParameter = () => {},
    moduleParameters = [],
    setModuleParameters = [],
    getGlobalModuleParameter = () => {},
    isWidget = false,
}) => {
    const parameter = isWidget ? 'hydrometryHorizonsWidget' : 'hydrometryHorizons'
    const foundParameter = parameters.find(p => p.parameter === parameter)?.value
    const foundModuleParameter = moduleParameters.find(p => p.parameter === parameter)?.value

    const [hydrometryHorizons, setHydrometryHorizons] = useState(foundParameter)
    const [moduleHydrometryHorizons, setModuleHydrometryHorizons] = useState(foundModuleParameter)

    useDebounce(() => {
        setParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalParameter(parameter, hydrometryHorizons),
        ])
    }, 200, [hydrometryHorizons])

    useDebounce(() => {
        setModuleParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalModuleParameter(parameter, moduleHydrometryHorizons),
        ])
    }, 200, [moduleHydrometryHorizons])

    return (
        <Grid2 container justifyContent='space-between' alignItems='center' columnSpacing={1}>
            <Grid2 size={0.5}>
                <HelpParameter image={hydrometryHorizonsHelp} />
            </Grid2>
            <Grid2 container size='grow'>
                <Typography variant='normalFormTitle'>{i18n.chartHorizons}</Typography>
            </Grid2>
            <Grid2 size={3.25}>
                <SimpleInput
                    value={hydrometryHorizons}
                    onChange={setHydrometryHorizons}
                    placeholder='7,30,90,365,HISTO'
                />
            </Grid2>
            <Grid2 size={3.25}>
                <SimpleInput
                    value={moduleHydrometryHorizons}
                    onChange={setModuleHydrometryHorizons}
                    placeholder='30,365,HISTO'
                />
            </Grid2>
            <Grid2 size={0.5} />
        </Grid2>
    )
}

HydroHorizons.propTypes = {
    parameters: PropTypes.arrayOf(PropTypes.shape({})),
    setParameters: PropTypes.func,
    getGlobalParameter: PropTypes.func,
    moduleParameters: PropTypes.arrayOf(PropTypes.shape({})),
    setModuleParameters: PropTypes.func,
    getGlobalModuleParameter: PropTypes.func,
    isWidget: PropTypes.bool,
}

const PluvioHorizons = ({
    parameters = [],
    setParameters = () => {},
    getGlobalParameter = () => {},
    moduleParameters = [],
    setModuleParameters = [],
    getGlobalModuleParameter = () => {},
    isWidget = false,
}) => {
    const parameter = isWidget ? 'pluviometryHorizonsWidget' : 'pluviometryHorizons'
    const foundParameter = parameters.find(p => p.parameter === parameter)?.value
    const foundModuleParameter = moduleParameters.find(p => p.parameter === parameter)?.value

    const [pluviometryHorizons, setPluviometryHorizons] = useState(foundParameter)
    const [modulePluviometryHorizons, setModulePluviometryHorizons] = useState(foundModuleParameter)

    useDebounce(() => {
        setParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalParameter(parameter, pluviometryHorizons),
        ])
    }, 200, [pluviometryHorizons])

    useDebounce(() => {
        setModuleParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalModuleParameter(parameter, modulePluviometryHorizons),
        ])
    }, 200, [modulePluviometryHorizons])

    return (
        <Grid2 container justifyContent='space-between' alignItems='center' columnSpacing={1}>
            <Grid2 size={0.5}>
                <HelpParameter image={pluviometryHorizonsHelp} />
            </Grid2>
            <Grid2 container size='grow'>
                <Typography variant='normalFormTitle'>{i18n.chartHorizons}</Typography>
            </Grid2>
            <Grid2 size={3.25}>
                <SimpleInput
                    value={pluviometryHorizons}
                    onChange={setPluviometryHorizons}
                    placeholder='7,30,90,365,HISTO'
                />
            </Grid2>
            <Grid2 size={3.25}>
                <SimpleInput
                    value={modulePluviometryHorizons}
                    onChange={setModulePluviometryHorizons}
                    placeholder='30,365,HISTO'
                />
            </Grid2>
            <Grid2 size={0.5} />
        </Grid2>
    )
}

PluvioHorizons.propTypes = {
    parameters: PropTypes.arrayOf(PropTypes.shape({})),
    setParameters: PropTypes.func,
    getGlobalParameter: PropTypes.func,
    moduleParameters: PropTypes.arrayOf(PropTypes.shape({})),
    setModuleParameters: PropTypes.func,
    getGlobalModuleParameter: PropTypes.func,
    isWidget: PropTypes.bool,
}

const PiezoLimitHisto = ({
    parameters = [],
    setParameters = () => {},
    getGlobalParameter = () => {},
    moduleParameters = [],
    setModuleParameters = [],
    getGlobalModuleParameter = () => {},
    isWidget = false,
}) => {
    const parameter = isWidget ? 'piezometryLimitHistoWidget' : 'piezometryLimitHisto'
    const foundParameter = parameters.find(p => p.parameter === parameter)?.value
    const foundModuleParameter = moduleParameters.find(p => p.parameter === parameter)?.value

    const [piezometryLimitHisto, setPiezometryLimitHisto] = useState(foundParameter)
    const [modulePiezometryLimitHisto, setModulePiezometryLimitHisto] = useState(foundModuleParameter)

    useDebounce(() => {
        setParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalParameter(parameter, piezometryLimitHisto),
        ])
    }, 200, [piezometryLimitHisto])

    useDebounce(() => {
        setModuleParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalModuleParameter(parameter, modulePiezometryLimitHisto),
        ])
    }, 200, [modulePiezometryLimitHisto])

    return (
        <Grid2 container justifyContent='space-between' alignItems='center' columnSpacing={1}>
            <Grid2 size={0.5}>
                <HelpParameter image={piezometryLimitHistoHelp} />
            </Grid2>
            <Grid2 container size='grow'>
                <Typography variant='normalFormTitle'>{i18n.chartHistoryLimit}</Typography>
            </Grid2>
            <Grid2 size={3.25}>
                <SimpleInput
                    value={piezometryLimitHisto}
                    onChange={setPiezometryLimitHisto}
                    placeholder='265'
                    type={INPUT_TYPES.int}
                />
            </Grid2>
            <Grid2 size={3.25}>
                <SimpleInput
                    value={modulePiezometryLimitHisto}
                    onChange={setModulePiezometryLimitHisto}
                    placeholder='265'
                    type={INPUT_TYPES.int}
                />
            </Grid2>
            <Grid2 size={0.5} />
        </Grid2>
    )
}

PiezoLimitHisto.propTypes = {
    parameters: PropTypes.arrayOf(PropTypes.shape({})),
    setParameters: PropTypes.func,
    getGlobalParameter: PropTypes.func,
    moduleParameters: PropTypes.arrayOf(PropTypes.shape({})),
    setModuleParameters: PropTypes.func,
    getGlobalModuleParameter: PropTypes.func,
    isWidget: PropTypes.bool,
}

const HydroLimitHisto = ({
    parameters = [],
    setParameters = () => {},
    getGlobalParameter = () => {},
    moduleParameters = [],
    setModuleParameters = [],
    getGlobalModuleParameter = () => {},
    isWidget = false,
}) => {
    const parameter = isWidget ? 'hydrometryLimitHistoWidget' : 'hydrometryLimitHisto'
    const foundParameter = parameters.find(p => p.parameter === parameter)?.value
    const foundModuleParameter = moduleParameters.find(p => p.parameter === parameter)?.value

    const [hydrometryLimitHisto, setHydrometryLimitHisto] = useState(foundParameter)
    const [moduleHydrometryLimitHisto, setModuleHydrometryLimitHisto] = useState(foundModuleParameter)

    useDebounce(() => {
        setParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalParameter(parameter, hydrometryLimitHisto),
        ])
    }, 200, [hydrometryLimitHisto])

    useDebounce(() => {
        setModuleParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalModuleParameter(parameter, moduleHydrometryLimitHisto),
        ])
    }, 200, [moduleHydrometryLimitHisto])

    return (
        <Grid2 container justifyContent='space-between' alignItems='center' columnSpacing={1}>
            <Grid2 size={0.5}>
                <HelpParameter image={hydrometryLimitHistoHelp} />
            </Grid2>
            <Grid2 container size='grow'>
                <Typography variant='normalFormTitle'>{i18n.chartHistoryLimit}</Typography>
            </Grid2>
            <Grid2 size={3.25}>
                <SimpleInput
                    value={hydrometryLimitHisto}
                    onChange={setHydrometryLimitHisto}
                    placeholder='265'
                    type={INPUT_TYPES.int}
                />
            </Grid2>
            <Grid2 size={3.25}>
                <SimpleInput
                    value={moduleHydrometryLimitHisto}
                    onChange={setModuleHydrometryLimitHisto}
                    placeholder='265'
                    type={INPUT_TYPES.int}
                />
            </Grid2>
            <Grid2 size={0.5} />
        </Grid2>
    )
}

HydroLimitHisto.propTypes = {
    parameters: PropTypes.arrayOf(PropTypes.shape({})),
    setParameters: PropTypes.func,
    getGlobalParameter: PropTypes.func,
    moduleParameters: PropTypes.arrayOf(PropTypes.shape({})),
    setModuleParameters: PropTypes.func,
    getGlobalModuleParameter: PropTypes.func,
    isWidget: PropTypes.bool,
}

const PluvioLimitHisto = ({
    parameters = [],
    setParameters = () => {},
    getGlobalParameter = () => {},
    moduleParameters = [],
    setModuleParameters = [],
    getGlobalModuleParameter = () => {},
    isWidget = false,
}) => {
    const parameter = isWidget ? 'pluviometryLimitHistoWidget' : 'pluviometryLimitHisto'
    const foundParameter = parameters.find(p => p.parameter === parameter)?.value
    const foundModuleParameter = moduleParameters.find(p => p.parameter === parameter)?.value

    const [pluviometryLimitHisto, setPluviometryLimitHisto] = useState(foundParameter)
    const [modulePluviometryLimitHisto, setModulePluviometryLimitHisto] = useState(foundModuleParameter)

    useDebounce(() => {
        setParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalParameter(parameter, pluviometryLimitHisto),
        ])
    }, 200, [pluviometryLimitHisto])

    useDebounce(() => {
        setModuleParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalModuleParameter(parameter, modulePluviometryLimitHisto),
        ])
    }, 200, [modulePluviometryLimitHisto])

    return (
        <Grid2 container justifyContent='space-between' alignItems='center' columnSpacing={1}>
            <Grid2 size={0.5}>
                <HelpParameter image={pluviometryLimitHistoHelp} />
            </Grid2>
            <Grid2 container size='grow'>
                <Typography variant='normalFormTitle'>{i18n.chartHistoryLimit}</Typography>
            </Grid2>
            <Grid2 size={3.25}>
                <SimpleInput
                    value={pluviometryLimitHisto}
                    onChange={setPluviometryLimitHisto}
                    placeholder='265'
                    type={INPUT_TYPES.int}
                />
            </Grid2>
            <Grid2 size={3.25}>
                <SimpleInput
                    value={modulePluviometryLimitHisto}
                    onChange={setModulePluviometryLimitHisto}
                    placeholder='265'
                    type={INPUT_TYPES.int}
                />
            </Grid2>
            <Grid2 size={0.5} />
        </Grid2>
    )
}

PluvioLimitHisto.propTypes = {
    parameters: PropTypes.arrayOf(PropTypes.shape({})),
    setParameters: PropTypes.func,
    getGlobalParameter: PropTypes.func,
    moduleParameters: PropTypes.arrayOf(PropTypes.shape({})),
    setModuleParameters: PropTypes.func,
    getGlobalModuleParameter: PropTypes.func,
    isWidget: PropTypes.bool,
}

const RecaptchaId = ({
    parameters = [],
    setParameters = () => {},
    getGlobalParameter = () => {},
    moduleParameters = [],
    setModuleParameters = [],
    getGlobalModuleParameter = () => {},
}) => {
    const parameter = 'idRecaptchaContact'
    const foundParameter = parameters.find(p => p.parameter === parameter)?.value
    const foundModuleParameter = moduleParameters.find(p => p.parameter === parameter)?.value

    const [idRecaptchaContact, setIdRecaptchaContact] = useState(foundParameter)
    const [moduleIdRecaptchaContact, setModuleIdRecaptchaContact] = useState(foundModuleParameter)

    useDebounce(() => {
        setParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalParameter(parameter, idRecaptchaContact),
        ])
    }, 200, [idRecaptchaContact])

    useDebounce(() => {
        setModuleParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalModuleParameter(parameter, moduleIdRecaptchaContact),
        ])
    }, 200, [moduleIdRecaptchaContact])

    return (
        <Grid2 container justifyContent='space-between' alignItems='center' columnSpacing={1}>
            <Grid2 size={0.5}>
                <HelpParameter image={idRecaptchaContactHelp} />
            </Grid2>
            <Grid2 container size='grow'>
                <Typography variant='normalFormTitle'>{i18n.recaptchaId}</Typography>
            </Grid2>
            <Grid2 size={3.25}>
                <SimpleInput
                    value={idRecaptchaContact}
                    onChange={setIdRecaptchaContact}
                    placeholder='6Lew_'
                />
            </Grid2>
            <Grid2 size={3.25}>
                <SimpleInput
                    value={moduleIdRecaptchaContact}
                    onChange={setModuleIdRecaptchaContact}
                    placeholder='6Lew_'
                />
            </Grid2>
            <Grid2 size={0.5}>
                <SeeOtherIconTooltip
                    title={i18n.seeRecaptcha}
                    link='https://console.cloud.google.com/security/recaptcha?authuser=2&hl=fr&project=qaptys-notifications&pli=1'
                />
            </Grid2>
        </Grid2>
    )
}

RecaptchaId.propTypes = {
    parameters: PropTypes.arrayOf(PropTypes.shape({})),
    setParameters: PropTypes.func,
    getGlobalParameter: PropTypes.func,
    moduleParameters: PropTypes.arrayOf(PropTypes.shape({})),
    setModuleParameters: PropTypes.func,
    getGlobalModuleParameter: PropTypes.func,
}

const WidgetPublicToken = ({
    parameters = [],
    setParameters = () => {},
    getGlobalParameter = () => {},
    moduleParameters = [],
    setModuleParameters = [],
    getGlobalModuleParameter = () => {},
}) => {
    const {
        applicativeUsers,
    } = useSelector(store => ({
        applicativeUsers: store.UserReducer.applicativeUsers,
    }), shallowEqual)

    const parameter = 'widgetPublicToken'
    const otherParameter = 'widgetPublicLogin'
    const foundParameter = parameters.find(p => p.parameter === parameter)?.value
    const foundModuleParameter = moduleParameters.find(p => p.parameter === parameter)?.value

    const [widgetPublicToken, setWidgetPublicToken] = useState(foundParameter)
    const [moduleWidgetPublicToken, setModuleWidgetPublicToken] = useState(foundModuleParameter)

    const widgets = applicativeUsers.filter(({ isApplicative, login }) => isApplicative && lowerCase(login).includes(WIDGET.conventionalName))

    useDebounce(() => {
        const widgetPublicLogin = widgets.find(w => w.token === widgetPublicToken)?.login
        const widgetPublicLoginParam = widgetPublicLogin ? getGlobalParameter(otherParameter, widgetPublicLogin) : {}
        setParameters(prev => [
            ...prev.filter(p => ![parameter, otherParameter].includes(p.parameter)),
            getGlobalParameter(parameter, widgetPublicToken),
            widgetPublicLoginParam,
        ])
    }, 200, [widgetPublicToken])

    useDebounce(() => {
        const moduleWidgetPublicLogin = widgets.find(w => w.token === moduleWidgetPublicToken)?.login
        const moduleWidgetPublicLoginParam = moduleWidgetPublicLogin ? getGlobalModuleParameter(otherParameter, moduleWidgetPublicLogin) : {}
        setModuleParameters(prev => [
            ...prev.filter(p => ![parameter, otherParameter].includes(p.parameter)),
            getGlobalModuleParameter(parameter, moduleWidgetPublicToken),
            moduleWidgetPublicLoginParam,
        ])
    }, 200, [moduleWidgetPublicToken])

    return (
        <Grid2 container justifyContent='space-between' alignItems='center' columnSpacing={1}>
            <Grid2 size={0.5}>
                <HelpParameter image={widgetPublicTokenHelp} />
            </Grid2>
            <Grid2 container size='grow'>
                <Typography variant='normalFormTitle'>Widget public</Typography>
            </Grid2>
            <Grid2 size={3.25}>
                <SuperMultiAutocomplete
                    options={widgets}
                    values={widgetPublicToken}
                    onChange={setWidgetPublicToken}
                    keyLabel='login'
                    keyValue='token'
                />
            </Grid2>
            <Grid2 size={3.25}>
                <SuperMultiAutocomplete
                    options={widgets}
                    values={moduleWidgetPublicToken}
                    onChange={setModuleWidgetPublicToken}
                    keyLabel='login'
                    keyValue='token'
                />
            </Grid2>
            <Grid2 size={0.5} />
        </Grid2>
    )
}

WidgetPublicToken.propTypes = {
    parameters: PropTypes.arrayOf(PropTypes.shape({})),
    setParameters: PropTypes.func,
    getGlobalParameter: PropTypes.func,
    moduleParameters: PropTypes.arrayOf(PropTypes.shape({})),
    setModuleParameters: PropTypes.func,
    getGlobalModuleParameter: PropTypes.func,
}

const ContactPageLink = ({
    parameters = [],
    setParameters = () => {},
    getGlobalParameter = () => {},
    moduleParameters = [],
    setModuleParameters = [],
    getGlobalModuleParameter = () => {},
}) => {
    const parameter = 'contactPageLink'
    const foundParameter = parameters.find(p => p.parameter === parameter)?.value
    const foundModuleParameter = moduleParameters.find(p => p.parameter === parameter)?.value

    const [contactPageLink, setContactPageLink] = useState(foundParameter)
    const [moduleContactPageLink, setModuleContactPageLink] = useState(foundModuleParameter)

    useDebounce(() => {
        setParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalParameter(parameter, contactPageLink),
        ])
    }, 200, [contactPageLink])

    useDebounce(() => {
        setModuleParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalModuleParameter(parameter, moduleContactPageLink),
        ])
    }, 200, [moduleContactPageLink])

    return (
        <Grid2 container justifyContent='space-between' alignItems='center' columnSpacing={1}>
            <Grid2 size={0.5}>
                <HelpParameter image={contactPageLinkHelp} />
            </Grid2>
            <Grid2 container size='grow'>
                <Typography variant='normalFormTitle'>{i18n.linkToAnotherContactPage}</Typography>
            </Grid2>
            <Grid2 size={3.25}>
                <SimpleInput
                    value={contactPageLink}
                    onChange={setContactPageLink}
                    placeholder='https://example.fr/contact/'
                />
            </Grid2>
            <Grid2 size={3.25}>
                <SimpleInput
                    value={moduleContactPageLink}
                    onChange={setModuleContactPageLink}
                    placeholder='https://example.fr/contact/'
                />
            </Grid2>
            <Grid2 size={0.5} />
        </Grid2>
    )
}

ContactPageLink.propTypes = {
    parameters: PropTypes.arrayOf(PropTypes.shape({})),
    setParameters: PropTypes.func,
    getGlobalParameter: PropTypes.func,
    moduleParameters: PropTypes.arrayOf(PropTypes.shape({})),
    setModuleParameters: PropTypes.func,
    getGlobalModuleParameter: PropTypes.func,
}

const ManagerUrl = ({
    parameters = [],
    setParameters = () => {},
    getGlobalParameter = () => {},
    moduleParameters = [],
    setModuleParameters = [],
    getGlobalModuleParameter = () => {},
}) => {
    const parameter = 'managerUrl'
    const foundParameter = parameters.find(p => p.parameter === parameter)?.value
    const foundModuleParameter = moduleParameters.find(p => p.parameter === parameter)?.value

    const [managerUrl, setManagerUrl] = useState(foundParameter)
    const [moduleManagerUrl, setModuleManagerUrl] = useState(foundModuleParameter)

    useDebounce(() => {
        setParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalParameter(parameter, managerUrl),
        ])
    }, 200, [managerUrl])

    useDebounce(() => {
        setModuleParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalModuleParameter(parameter, moduleManagerUrl),
        ])
    }, 200, [moduleManagerUrl])

    return (
        <Grid2 container justifyContent='space-between' alignItems='center' columnSpacing={1}>
            <Grid2 size={0.5} />
            <Grid2 container size='grow'>
                <Typography variant='normalFormTitle'>{i18n.managerUrl}</Typography>
            </Grid2>
            <Grid2 size={3.25}>
                <SimpleInput
                    value={managerUrl}
                    onChange={setManagerUrl}
                    placeholder='https://example.fr/'
                />
            </Grid2>
            <Grid2 size={3.25}>
                <SimpleInput
                    value={moduleManagerUrl}
                    onChange={setModuleManagerUrl}
                    placeholder='https://example.fr/'
                />
            </Grid2>
            <Grid2 size={0.5} />
        </Grid2>
    )
}

ManagerUrl.propTypes = {
    parameters: PropTypes.arrayOf(PropTypes.shape({})),
    setParameters: PropTypes.func,
    getGlobalParameter: PropTypes.func,
    moduleParameters: PropTypes.arrayOf(PropTypes.shape({})),
    setModuleParameters: PropTypes.func,
    getGlobalModuleParameter: PropTypes.func,
}

const NotDisplayWidgetHomePage = ({
    parameters = [],
    setParameters = () => {},
    getGlobalParameter = () => {},
    moduleParameters = [],
    setModuleParameters = [],
    getGlobalModuleParameter = () => {},
}) => {
    const parameter = 'notDisplayWidgetHomePage'
    const foundParameter = parameters.find(p => p.parameter === parameter)?.value
    const foundModuleParameter = moduleParameters.find(p => p.parameter === parameter)?.value

    const [notDisplayWidgetHomePage, setNotDisplayWidgetHomePage] = useState(foundParameter === 'true')
    const [moduleNotDisplayWidgetHomePage, setModuleNotDisplayWidgetHomePage] = useState(foundModuleParameter === 'true')

    useDebounce(() => {
        setParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalParameter(parameter, `${notDisplayWidgetHomePage}`),
        ])
    }, 200, [notDisplayWidgetHomePage])

    useDebounce(() => {
        setModuleParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalModuleParameter(parameter, `${moduleNotDisplayWidgetHomePage}`),
        ])
    }, 200, [moduleNotDisplayWidgetHomePage])

    return (
        <Grid2 container justifyContent='space-between' alignItems='center' columnSpacing={1}>
            <Grid2 size={0.5}>
                <HelpParameter image={notDisplayWidgetHomePageHelp} />
            </Grid2>
            <Grid2 container size='grow'>
                <Typography variant='normalFormTitle'>{i18n.startOnPublicWidget}</Typography>
            </Grid2>
            <Grid2 container size={3.25}>
                <Box onClick={() => setNotDisplayWidgetHomePage(prev => prev ? undefined : !prev)}>
                    <Switch checked={!notDisplayWidgetHomePage} />
                </Box>
            </Grid2>
            <Grid2 container size={3.25}>
                <Box onClick={() => setModuleNotDisplayWidgetHomePage(prev => prev ? undefined : !prev)}>
                    <Switch checked={!moduleNotDisplayWidgetHomePage} />
                </Box>
            </Grid2>
            <Grid2 size={0.5} />
        </Grid2>
    )
}

NotDisplayWidgetHomePage.propTypes = {
    parameters: PropTypes.arrayOf(PropTypes.shape({})),
    setParameters: PropTypes.func,
    getGlobalParameter: PropTypes.func,
    moduleParameters: PropTypes.arrayOf(PropTypes.shape({})),
    setModuleParameters: PropTypes.func,
    getGlobalModuleParameter: PropTypes.func,
}

const DefaultMapXCoordinate = ({
    parameters = [],
    setParameters = () => {},
    getGlobalParameter = () => {},
    moduleParameters = [],
    setModuleParameters = [],
    getGlobalModuleParameter = () => {},
}) => {
    const parameter = 'defaultMapXCoordinate'
    const foundParameter = parameters.find(p => p.parameter === parameter)?.value
    const foundModuleParameter = moduleParameters.find(p => p.parameter === parameter)?.value

    const [defaultMapXCoordinate, setDefaultMapXCoordinate] = useState(foundParameter)
    const [moduleDefaultMapXCoordinate, setModuleDefaultMapXCoordinate] = useState(foundModuleParameter)

    useDebounce(() => {
        setParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalParameter(parameter, defaultMapXCoordinate),
        ])
    }, 200, [defaultMapXCoordinate])

    useDebounce(() => {
        setModuleParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalModuleParameter(parameter, moduleDefaultMapXCoordinate),
        ])
    }, 200, [moduleDefaultMapXCoordinate])

    return (
        <Grid2 container justifyContent='space-between' alignItems='center' columnSpacing={1}>
            <Grid2 size={0.5} />
            <Grid2 container size='grow'>
                <Typography variant='normalFormTitle'>{i18n.defaultAxis}</Typography>
            </Grid2>
            <Grid2 size={3.25}>
                <SimpleInput
                    value={defaultMapXCoordinate}
                    onChange={setDefaultMapXCoordinate}
                    placeholder='2.6013202230563772'
                    type={INPUT_TYPES.float}
                />
            </Grid2>
            <Grid2 size={3.25}>
                <SimpleInput
                    value={moduleDefaultMapXCoordinate}
                    onChange={setModuleDefaultMapXCoordinate}
                    placeholder='2.6013202230563772'
                    type={INPUT_TYPES.float}
                />
            </Grid2>
            <Grid2 size={0.5} />
        </Grid2>
    )
}

DefaultMapXCoordinate.propTypes = {
    parameters: PropTypes.arrayOf(PropTypes.shape({})),
    setParameters: PropTypes.func,
    getGlobalParameter: PropTypes.func,
    moduleParameters: PropTypes.arrayOf(PropTypes.shape({})),
    setModuleParameters: PropTypes.func,
    getGlobalModuleParameter: PropTypes.func,
}

const DefaultMapYCoordinate = ({
    parameters = [],
    setParameters = () => {},
    getGlobalParameter = () => {},
    moduleParameters = [],
    setModuleParameters = [],
    getGlobalModuleParameter = () => {},
}) => {
    const parameter = 'defaultMapYCoordinate'
    const foundParameter = parameters.find(p => p.parameter === parameter)?.value
    const foundModuleParameter = moduleParameters.find(p => p.parameter === parameter)?.value

    const [defaultMapYCoordinate, setDefaultMapYCoordinate] = useState(foundParameter)
    const [moduleDefaultMapYCoordinate, setModuleDefaultMapYCoordinate] = useState(foundModuleParameter)

    useDebounce(() => {
        setParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalParameter(parameter, defaultMapYCoordinate),
        ])
    }, 200, [defaultMapYCoordinate])

    useDebounce(() => {
        setModuleParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalModuleParameter(parameter, moduleDefaultMapYCoordinate),
        ])
    }, 200, [moduleDefaultMapYCoordinate])

    return (
        <Grid2 container justifyContent='space-between' alignItems='center' columnSpacing={1}>
            <Grid2 size={0.5} />
            <Grid2 container size='grow'>
                <Typography variant='normalFormTitle'>{i18n.defaultOrderedAxis}</Typography>
            </Grid2>
            <Grid2 size={3.25}>
                <SimpleInput
                    value={defaultMapYCoordinate}
                    onChange={setDefaultMapYCoordinate}
                    placeholder='48.01685291740509'
                    type={INPUT_TYPES.float}
                />
            </Grid2>
            <Grid2 size={3.25}>
                <SimpleInput
                    value={moduleDefaultMapYCoordinate}
                    onChange={setModuleDefaultMapYCoordinate}
                    placeholder='48.01685291740509'
                    type={INPUT_TYPES.float}
                />
            </Grid2>
            <Grid2 size={0.5} />
        </Grid2>
    )
}

DefaultMapYCoordinate.propTypes = {
    parameters: PropTypes.arrayOf(PropTypes.shape({})),
    setParameters: PropTypes.func,
    getGlobalParameter: PropTypes.func,
    moduleParameters: PropTypes.arrayOf(PropTypes.shape({})),
    setModuleParameters: PropTypes.func,
    getGlobalModuleParameter: PropTypes.func,
}

const DefaultMapZoom = ({
    parameters = [],
    setParameters = () => {},
    getGlobalParameter = () => {},
    moduleParameters = [],
    setModuleParameters = [],
    getGlobalModuleParameter = () => {},
}) => {
    const parameter = 'defaultMapZoom'
    const foundParameter = parameters.find(p => p.parameter === parameter)?.value
    const foundModuleParameter = moduleParameters.find(p => p.parameter === parameter)?.value

    const [defaultMapZoom, setDefaultMapZoom] = useState(foundParameter)
    const [moduleDefaultMapZoom, setModuleDefaultMapZoom] = useState(foundModuleParameter)

    useDebounce(() => {
        setParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalParameter(parameter, defaultMapZoom),
        ])
    }, 200, [defaultMapZoom])

    useDebounce(() => {
        setModuleParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalModuleParameter(parameter, moduleDefaultMapZoom),
        ])
    }, 200, [moduleDefaultMapZoom])

    return (
        <Grid2 container justifyContent='space-between' alignItems='center' columnSpacing={1}>
            <Grid2 size={0.5} />
            <Grid2 container size='grow'>
                <Typography variant='normalFormTitle'>{i18n.defaultZoom}</Typography>
            </Grid2>
            <Grid2 size={3.25}>
                <SimpleInput
                    value={defaultMapZoom}
                    onChange={setDefaultMapZoom}
                    placeholder='6.070309681714787'
                    type={INPUT_TYPES.float}
                />
            </Grid2>
            <Grid2 size={3.25}>
                <SimpleInput
                    value={moduleDefaultMapZoom}
                    onChange={setModuleDefaultMapZoom}
                    placeholder='6.070309681714787'
                    type={INPUT_TYPES.float}
                />
            </Grid2>
            <Grid2 size={0.5} />
        </Grid2>
    )
}

DefaultMapZoom.propTypes = {
    parameters: PropTypes.arrayOf(PropTypes.shape({})),
    setParameters: PropTypes.func,
    getGlobalParameter: PropTypes.func,
    moduleParameters: PropTypes.arrayOf(PropTypes.shape({})),
    setModuleParameters: PropTypes.func,
    getGlobalModuleParameter: PropTypes.func,
}

const MAX_POPOVER_HEIGHT = { xl: 600, lg: 400 }
const roundDivStyle = {
    width: '1rem',
    height: '1rem',
    borderRadius: '50rem',
}

const DAY = 'day'
const HOUR = 'hour'

const FREQUENCY_STATIONS_TYPES = {
    piezo: 'PIEZO',
    hydro: 'HYDRO',
    pluvio: 'PLUVIO',
}

const DEFAULT_DISPLAY_MODES = {
    auto: true,
    brute: false,
    min: false,
    max: false,
    average: false,

    personalizedGrouping: false,
    personalizedGroupingValue: '',
}
const DEFAULT_PLUVIO_DISPLAY_MODES = {
    auto: true,
    all: false,
    sumHour: false,
    sumDay: false,
    sumWeek: false,
    sumDecade: false,
    sumMonth: false,
    sumYear: false,
    personalizedGrouping: false,
}

const FrequencyDatasChoices = ({
    defaultDisplayMode = [],

    displayModes = [],
    setDisplayModes = () => {},

    optionDayHourSelected = HOUR,
    setOptionDayHourSelected = () => {},
    hoursCumul = 1,
    setHoursCumul = () => {},
    optionCumulSelected = CUMUL_PERSO_MAX,
    setOptionCumulSelected = () => {},
}) => {
    const optionDayHour = [
        { value: HOUR, label: i18n.hour },
        { value: DAY, label: i18n.day },
    ]
    const optionCumul = [
        { value: CUMUL_PERSO_MAX, label: i18n.max },
        { value: CUMUL_PERSO_MIN, label: i18n.min },
        { value: CUMUL_PERSO_SUM, label: i18n.sum },
        { value: CUMUL_PERSO_AVERAGE, label: i18n.average },
    ]

    const getDisplayModeColor = displayMode => {
        switch (displayMode) {
            case 'min':
                return PASTEL_COLOR.red
            case 'max':
                return PASTEL_COLOR.blue
            case 'average':
                return PASTEL_COLOR.green
            default:
                return 'black'
        }
    }

    return (
        <Stack spacing={2}>
            <SimpleCheckbox
                label='Auto'
                labelStyle={{ fontWeight: 'normal' }}
                beforeLabel={(
                    <Box sx={{ ...roundDivStyle, backgroundColor: 'black' }} />
                )}
                afterLabel={(
                    <Tooltip
                        placement='right'
                        title={i18n.descriptionCumulAuto}
                    >
                        <InfoRoundedIcon sx={{ fontSize: '18px', color: 'grey' }} />
                    </Tooltip>
                )}
                checked={displayModes.auto}
                onToggle={() => setDisplayModes(defaultDisplayMode)}
            />
            {keys(omit(displayModes, ['auto', 'personalizedGrouping', 'personalizedGroupingValue'])).map(key => (
                <SimpleCheckbox
                    key={key}
                    label={getI18nOrLabel(key)}
                    labelStyle={{ fontWeight: 'normal' }}
                    beforeLabel={(
                        <Box style={{ ...roundDivStyle, backgroundColor: getDisplayModeColor(key) }} />
                    )}
                    afterLabel={key === 'brute' && (
                        <WarningRoundedIcon sx={{ fontSize: '18px', color: 'orange' }} />
                    )}
                    checked={displayModes[key]}
                    onToggle={event => setDisplayModes({ [key]: event.target.checked })}
                />
            ))}
            <SimpleCheckbox
                label={i18n.personalizedGrouping}
                labelStyle={{ fontWeight: 'normal' }}
                beforeLabel={(
                    <Box style={{ ...roundDivStyle, backgroundColor: PASTEL_COLOR.orange }} />
                )}
                checked={displayModes.personalizedGrouping}
                onToggle={event => {
                    const day = `${optionCumulSelected}_${hoursCumul * 24}`
                    const hour = `${optionCumulSelected}_${hoursCumul}`
                    setDisplayModes({ personalizedGrouping: event.target.checked }, { personalizedGroupingValue: optionDayHourSelected === DAY ? day : hour })
                }}
            />
            {!!displayModes.personalizedGrouping && (
                <FieldsetMUI
                    title={(
                        <Stack direction='row' alignItems='center' gap={0.5} sx={{ padding: '0 0.5rem' }}>
                            <QueryStatsRoundedIcon />
                            {i18n.modeGrouping}
                        </Stack>
                    )}
                >
                    <Grid2 container rowSpacing={1} justifyContent='center' sx={{ padding: '0.5rem 0 0' }}>
                        <Grid2>
                            <NewRadioButtons
                                label={i18n.modeDaysHours}
                                value={optionDayHourSelected}
                                items={optionDayHour}
                                onChange={setOptionDayHourSelected}
                            />
                        </Grid2>
                        <Grid2 container columnSpacing={1} size={12} sx={{ padding: '10px 10px' }}>
                            <Grid2 size={12}>
                                <SuperMultiAutocomplete
                                    label={i18n.modeGrouping}
                                    values={optionCumulSelected}
                                    keyValue='value'
                                    onChange={setOptionCumulSelected}
                                    options={optionCumul}
                                    disablePortal={false}
                                />
                            </Grid2>
                            <Grid2 size={12}>
                                <SimpleInput
                                    title={i18n.numberHoursDays}
                                    value={hoursCumul}
                                    onChange={setHoursCumul}
                                    placeholder='1'
                                    type={INPUT_TYPES.int}
                                />
                            </Grid2>
                        </Grid2>
                    </Grid2>
                </FieldsetMUI>
            )}
        </Stack>
    )
}

FrequencyDatasChoices.propTypes = {
    defaultDisplayMode: PropTypes.arrayOf(PropTypes.shape({})),
    displayModes: PropTypes.arrayOf(PropTypes.shape({})),
    setDisplayModes: PropTypes.func,
    optionDayHourSelected: PropTypes.string,
    setOptionDayHourSelected: PropTypes.func,
    hoursCumul: PropTypes.number,
    setHoursCumul: PropTypes.func,
    optionCumulSelected: PropTypes.string,
    setOptionCumulSelected: PropTypes.func,
}

const FrequencyDatas = ({
    module = { module: 'OBSERVATOIRE' },
    stationType = FREQUENCY_STATIONS_TYPES.piezo,

    parameters = [],
    setParameters = () => {},
    getGlobalParameter = () => {},

    moduleParameters = [],
    setModuleParameters = [],
    getGlobalModuleParameter = () => {},

    displayModuleParameter = false,
}) => {
    const parameter = `FREQUENCE_DONNEES_${stationType}_${toUpper(module.module)}`
    const foundParameter = parameters.find(p => p.parameter === parameter)?.value
    const foundModuleParameter = moduleParameters.find(p => p.parameter === parameter)?.value

    const defaultDisplayMode = useMemo(() => {
        switch (stationType) {
            case FREQUENCY_STATIONS_TYPES.piezo:
                return DEFAULT_DISPLAY_MODES
            case FREQUENCY_STATIONS_TYPES.hydro:
                return DEFAULT_DISPLAY_MODES
            case FREQUENCY_STATIONS_TYPES.pluvio:
                return DEFAULT_PLUVIO_DISPLAY_MODES
            default:
                return undefined
        }
    }, [])

    const displayModes = useMemo(() => foundParameter ? JSON.parse(foundParameter) : defaultDisplayMode, [])
    const moduleDisplayModes = useMemo(() => foundModuleParameter ? JSON.parse(foundModuleParameter) : defaultDisplayMode, [])

    const [settingsEl, setSettingsEl] = useState(undefined)
    const [tempDisplayModes, setTempDiplayModes] = useState(displayModes)
    const [optionDayHourSelected, setOptionDayHourSelected] = useState(HOUR)
    const [hoursCumul, setHoursCumul] = useState(1)
    const [optionCumulSelected, setOptionCumulSelected] = useState(CUMUL_PERSO_MAX)

    const [moduleSettingsEl, setModuleSettingsEl] = useState(undefined)
    const [moduleTempDisplayModes, setModuleTempDiplayModes] = useState(moduleDisplayModes)
    const [moduleOptionDayHourSelected, setModuleOptionDayHourSelected] = useState(HOUR)
    const [moduleHoursCumul, setModuleHoursCumul] = useState(1)
    const [moduleOptionCumulSelected, setModuleOptionCumulSelected] = useState(CUMUL_PERSO_MAX)

    useDebounce(() => {
        setParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalParameter(parameter, JSON.stringify(tempDisplayModes)),
        ])
    }, 200, [tempDisplayModes])

    useDebounce(() => {
        setModuleParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalModuleParameter(parameter, JSON.stringify(moduleTempDisplayModes)),
        ])
    }, 200, [moduleTempDisplayModes])

    const onChangeDisplayMode = (changes, changes2 = {}) => {
        const newDisplay = stationType === FREQUENCY_STATIONS_TYPES.pluvio ? (() => {
            return {
                ...mapValues(tempDisplayModes, () => false),
                [keys(changes)[0]]: true,
                ...changes2,
            }
        })() : (() => {
            const tmpNewDisplay = { ...tempDisplayModes, ...changes, ...changes2 }
            return {
                ...tmpNewDisplay,
                auto: (!tmpNewDisplay.brute && !tmpNewDisplay.min && !tmpNewDisplay.max && !tmpNewDisplay.average && !tmpNewDisplay.personalizedGrouping),
            }
        })()
        setTempDiplayModes(newDisplay)
    }

    const onChangeModuleDisplayMode = (changes, changes2 = {}) => {
        const newDisplay = stationType === FREQUENCY_STATIONS_TYPES.pluvio ? (() => {
            return {
                ...mapValues(moduleTempDisplayModes, () => false),
                [keys(changes)[0]]: true,
                ...changes2,
            }
        })() : (() => {
            const tmpNewDisplay = { ...moduleTempDisplayModes, ...changes, ...changes2 }
            return {
                ...tmpNewDisplay,
                auto: (!tmpNewDisplay.brute && !tmpNewDisplay.min && !tmpNewDisplay.max && !tmpNewDisplay.average && !tmpNewDisplay.personalizedGrouping),
            }
        })()
        setModuleTempDiplayModes(newDisplay)
    }

    useEffect(() => {
        if (displayModes.personalizedGrouping) {
            const groupingValues = displayModes.personalizedGroupingValue.split('_')
            const [cumulValues, dayValues] = chunk(groupingValues, 3)
            const dayHour = parseInt(dayValues[0])
            const cumulSelected = cumulValues.join('_')
            setHoursCumul(dayHour)
            setOptionCumulSelected(cumulSelected)
        }
    }, [displayModes])

    useEffect(() => {
        if (moduleDisplayModes.personalizedGrouping) {
            const groupingValues = moduleDisplayModes.personalizedGroupingValue.split('_')
            const [cumulValues, dayValues] = chunk(groupingValues, 3)
            const dayHour = parseInt(dayValues[0])
            const cumulSelected = cumulValues.join('_')
            setModuleHoursCumul(dayHour)
            setModuleOptionCumulSelected(cumulSelected)
        }
    }, [moduleDisplayModes])

    useEffect(() => {
        if (tempDisplayModes?.personalizedGrouping) {
            const day = `${optionCumulSelected}_${hoursCumul * 24}`
            const hour = `${optionCumulSelected}_${hoursCumul}`
            onChangeDisplayMode({ personalizedGrouping: true }, { personalizedGroupingValue: optionDayHourSelected === DAY ? day : hour })
        }
    }, [optionDayHourSelected, hoursCumul, optionCumulSelected])

    useEffect(() => {
        if (moduleTempDisplayModes?.personalizedGrouping) {
            const day = `${moduleOptionCumulSelected}_${moduleHoursCumul * 24}`
            const hour = `${moduleOptionCumulSelected}_${moduleHoursCumul}`
            onChangeModuleDisplayMode({ personalizedGrouping: true }, { personalizedGroupingValue: moduleOptionDayHourSelected === DAY ? day : hour })
        }
    }, [moduleOptionDayHourSelected, moduleHoursCumul, moduleOptionCumulSelected])

    return (
        <Grid2 container justifyContent='space-between' alignItems='center' columnSpacing={1}>
            <Grid2 size={0.5} />
            <Grid2 container size='grow'>
                <Typography variant='normalFormTitle'>{i18n.defaultDataFrequency}</Typography>
            </Grid2>
            <Grid2 size={3.25}>
                <Chip
                    label={i18n.editFrequencies}
                    icon={<EditRoundedIcon />}
                    onClick={event => setSettingsEl(event.currentTarget)}
                    clickable
                    variant='new-filled'
                    sx={{
                        color: mainBlue,
                        backgroundColor: `rgba(${brutRgbMainBlue}, 0.06)`,
                        backgroundColorHover: `rgba(${brutRgbMainBlue}, 0.16)`,
                    }}
                />
                <Popover
                    open={!!settingsEl}
                    anchorEl={settingsEl}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                    slotProps={{
                        paper: {
                            sx: {
                                width: '350px',
                                maxHeight: MAX_POPOVER_HEIGHT,
                                padding: '1rem',
                                border: `solid 1px rgba(${brutRgbMainBlue}, 0.6)`,
                                borderRadius: LARGE_RADIUS,
                            },
                        },
                    }}
                    onClose={() => setSettingsEl(undefined)}
                >
                    <FrequencyDatasChoices
                        defaultDisplayMode={defaultDisplayMode}

                        displayModes={tempDisplayModes}
                        setDisplayModes={onChangeDisplayMode}

                        optionDayHourSelected={optionDayHourSelected}
                        setOptionDayHourSelected={setOptionDayHourSelected}
                        hoursCumul={hoursCumul}
                        setHoursCumul={setHoursCumul}
                        optionCumulSelected={optionCumulSelected}
                        setOptionCumulSelected={setOptionCumulSelected}
                    />
                </Popover>
            </Grid2>
            {!!displayModuleParameter && (
                <Grid2 size={3.25}>
                    <Chip
                        label={i18n.editFrequencies}
                        icon={<EditRoundedIcon />}
                        onClick={event => setModuleSettingsEl(event.currentTarget)}
                        clickable
                        variant='new-filled'
                        sx={{
                            color: mainBlue,
                            backgroundColor: `rgba(${brutRgbMainBlue}, 0.06)`,
                            backgroundColorHover: `rgba(${brutRgbMainBlue}, 0.16)`,
                        }}
                    />
                    <Popover
                        open={!!moduleSettingsEl}
                        anchorEl={moduleSettingsEl}
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'left',
                        }}
                        slotProps={{
                            paper: {
                                sx: {
                                    width: '350px',
                                    maxHeight: MAX_POPOVER_HEIGHT,
                                    padding: '1rem',
                                    border: `solid 1px rgba(${brutRgbMainBlue}, 0.6)`,
                                    borderRadius: LARGE_RADIUS,
                                },
                            },
                        }}
                        onClose={() => setModuleSettingsEl(undefined)}
                    >
                        <FrequencyDatasChoices
                            defaultDisplayMode={defaultDisplayMode}

                            displayModes={moduleTempDisplayModes}
                            setDisplayModes={onChangeModuleDisplayMode}

                            optionDayHourSelected={moduleOptionDayHourSelected}
                            setOptionDayHourSelected={setModuleOptionDayHourSelected}
                            hoursCumul={moduleHoursCumul}
                            setHoursCumul={setModuleHoursCumul}
                            optionCumulSelected={moduleOptionCumulSelected}
                            setOptionCumulSelected={setModuleOptionCumulSelected}
                        />
                    </Popover>
                </Grid2>
            )}
            <Grid2 offset={!displayModuleParameter && 3.25} size={0.5} />
        </Grid2>
    )
}

FrequencyDatas.propTypes = {
    module: PropTypes.shape({
        module: PropTypes.string,
    }),
    stationType: PropTypes.string,
    parameters: PropTypes.arrayOf(PropTypes.shape({})),
    setParameters: PropTypes.func,
    moduleParameters: PropTypes.arrayOf(PropTypes.shape({})),
    setModuleParameters: PropTypes.func,
    getGlobalParameter: PropTypes.func,
    getGlobalModuleParameter: PropTypes.func,
    displayModuleParameter: PropTypes.bool,
}

const UdiReferenceThreshold = ({
    parameters = [],
    setParameters = () => {},
    getGlobalParameter = () => {},
    moduleParameters = [],
    setModuleParameters = [],
    getGlobalModuleParameter = () => {},
}) => {
    const {
        qualityThresholds,
    } = useSelector(store => ({
        qualityThresholds: store.QualityReducer.thresholds,
    }), shallowEqual)

    const parameter = 'UDI_REFERENCE_THRESHOLD'
    const foundParameter = parameters.find(p => p.parameter === parameter)?.value
    const foundModuleParameter = moduleParameters.find(p => p.parameter === parameter)?.value

    const [udiReferenceThreshold, setUdiReferenceThreshold] = useState(foundParameter && foundParameter.split(',').map((id) => Number(id)))
    const [moduleUdiReferenceThreshold, setModuleUdiReferenceThreshold] = useState(foundModuleParameter && foundModuleParameter.split(',').map((id) => Number(id)))

    useDebounce(() => {
        setParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalParameter(parameter, udiReferenceThreshold.join(',')),
        ])
    }, 200, [udiReferenceThreshold])

    useDebounce(() => {
        setModuleParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalModuleParameter(parameter, moduleUdiReferenceThreshold.join(',')),
        ])
    }, 200, [moduleUdiReferenceThreshold])

    return (
        <Grid2 container justifyContent='space-between' alignItems='center' columnSpacing={1}>
            <Grid2 size={0.5}>
                <HelpParameter image={categoriesCmsDocumentsHelp} />
            </Grid2>
            <Grid2 container size='grow'>
                <Typography variant='normalFormTitle'>{i18n.referenceThresholds}</Typography>
            </Grid2>
            <Grid2 size={3.25}>
                <SuperMultiAutocomplete
                    options={qualityThresholds}
                    values={udiReferenceThreshold}
                    onChange={setUdiReferenceThreshold}
                    keyLabel='name'
                    keyValue='code'
                    multiple
                    limit={1}
                />
            </Grid2>
            <Grid2 size={3.25}>
                <SuperMultiAutocomplete
                    options={qualityThresholds}
                    values={moduleUdiReferenceThreshold}
                    onChange={setModuleUdiReferenceThreshold}
                    keyLabel='name'
                    keyValue='code'
                    multiple
                    limit={1}
                />
            </Grid2>
            <Grid2 size={0.5}>
                <SeeOtherIconTooltip
                    title={i18n.seeQualityThresholds}
                    link={`${usedUrl}#/quality/qualityOptions/threshold`}
                />
            </Grid2>
        </Grid2>
    )
}

UdiReferenceThreshold.propTypes = {
    parameters: PropTypes.arrayOf(PropTypes.shape({})),
    setParameters: PropTypes.func,
    getGlobalParameter: PropTypes.func,
    moduleParameters: PropTypes.arrayOf(PropTypes.shape({})),
    setModuleParameters: PropTypes.func,
    getGlobalModuleParameter: PropTypes.func,
}

const ContributorTypeOperator = ({
    parameters = [],
    setParameters = () => {},
    getGlobalParameter = () => {},
    moduleParameters = [],
    setModuleParameters = [],
    getGlobalModuleParameter = () => {},
}) => {
    const parameter = 'contributorTypeOperator'
    const foundParameter = parameters.find(p => p.parameter === parameter)?.value
    const foundModuleParameter = moduleParameters.find(p => p.parameter === parameter)?.value

    const [contributorTypeOperator, setContributorTypeOperator] = useState(foundParameter && parseInt(foundParameter))
    const [moduleContributorTypeOperator, setModuleContributorTypeOperator] = useState(foundModuleParameter && parseInt(foundModuleParameter))

    useDebounce(() => {
        setParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalParameter(parameter, contributorTypeOperator),
        ])
    }, 200, [contributorTypeOperator])

    useDebounce(() => {
        setModuleParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalModuleParameter(parameter, moduleContributorTypeOperator),
        ])
    }, 200, [moduleContributorTypeOperator])

    const contributorTypeList = useSandreList(SANDRE.CONTRIBUTOR_TYPES).map(c => ({ ...c, name: `[${c.code}] ${c.name}` }))

    return (
        <Grid2 container justifyContent='space-between' alignItems='center' columnSpacing={1}>
            <Grid2 size={0.5}>
                <HelpParameter image={idDailyMinHelp} />
            </Grid2>
            <Grid2 size='grow'><Typography variant='normalFormTitle'>{i18n.operatorTypeContributor}</Typography></Grid2>
            <Grid2 size={3.25}>
                <SuperMultiAutocomplete
                    options={contributorTypeList}
                    values={contributorTypeOperator}
                    onChange={setContributorTypeOperator}
                    keyValue='code'
                />
            </Grid2>
            <Grid2 size={3.25}>
                <SuperMultiAutocomplete
                    options={contributorTypeList}
                    values={moduleContributorTypeOperator}
                    onChange={setModuleContributorTypeOperator}
                    keyValue='code'
                />
            </Grid2>
            <Grid2 size={0.5}>
                <SeeOtherIconTooltip
                    title={i18n.seeContributorsTypes}
                    link={`${usedUrl}#/referencial/lexicons`}
                />
            </Grid2>
        </Grid2>
    )
}

ContributorTypeOperator.propTypes = {
    parameters: PropTypes.arrayOf(PropTypes.shape({})),
    setParameters: PropTypes.func,
    getGlobalParameter: PropTypes.func,
    moduleParameters: PropTypes.arrayOf(PropTypes.shape({})),
    setModuleParameters: PropTypes.func,
    getGlobalModuleParameter: PropTypes.func,
}

const ContributorTypeAdministrator = ({
    parameters = [],
    setParameters = () => {},
    getGlobalParameter = () => {},
    moduleParameters = [],
    setModuleParameters = [],
    getGlobalModuleParameter = () => {},
}) => {
    const parameter = 'contributorTypeAdministrator'
    const foundParameter = parameters.find(p => p.parameter === parameter)?.value
    const foundModuleParameter = moduleParameters.find(p => p.parameter === parameter)?.value

    const [contributorTypeAdministrator, setContributorTypeAdministrator] = useState(foundParameter && parseInt(foundParameter))
    const [moduleContributorTypeAdministrator, setModuleContributorTypeAdministrator] = useState(foundModuleParameter && parseInt(foundModuleParameter))

    useDebounce(() => {
        setParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalParameter(parameter, contributorTypeAdministrator),
        ])
    }, 200, [contributorTypeAdministrator])

    useDebounce(() => {
        setModuleParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalModuleParameter(parameter, moduleContributorTypeAdministrator),
        ])
    }, 200, [moduleContributorTypeAdministrator])

    const contributorTypeList = useSandreList(SANDRE.CONTRIBUTOR_TYPES).map(c => ({ ...c, name: `[${c.code}] ${c.name}` }))

    return (
        <Grid2 container justifyContent='space-between' alignItems='center' columnSpacing={1}>
            <Grid2 size={0.5}>
                <HelpParameter image={idDailyMinHelp} />
            </Grid2>
            <Grid2 size='grow'><Typography variant='normalFormTitle'>{i18n.managerTypeContributor}</Typography></Grid2>
            <Grid2 size={3.25}>
                <SuperMultiAutocomplete
                    options={contributorTypeList}
                    values={contributorTypeAdministrator}
                    onChange={setContributorTypeAdministrator}
                    keyValue='code'
                />
            </Grid2>
            <Grid2 size={3.25}>
                <SuperMultiAutocomplete
                    options={contributorTypeList}
                    values={moduleContributorTypeAdministrator}
                    onChange={setModuleContributorTypeAdministrator}
                    keyValue='code'
                />
            </Grid2>
            <Grid2 size={0.5}>
                <SeeOtherIconTooltip
                    title={i18n.seeContributorsTypes}
                    link={`${usedUrl}#/referencial/lexicons`}
                />
            </Grid2>
        </Grid2>
    )
}

ContributorTypeAdministrator.propTypes = {
    parameters: PropTypes.arrayOf(PropTypes.shape({})),
    setParameters: PropTypes.func,
    getGlobalParameter: PropTypes.func,
    moduleParameters: PropTypes.arrayOf(PropTypes.shape({})),
    setModuleParameters: PropTypes.func,
    getGlobalModuleParameter: PropTypes.func,
}

const ShowDataTypesRules = ({
    link,
}) => !!link && (
    <Grid2 container justifyContent='space-between' alignItems='center' columnSpacing={1}>
        <Grid2 size={0.5} />
        <Grid2 size='grow'><Typography variant='normalFormTitle'>{i18n.additionelDataToDisplay}</Typography></Grid2>
        <Grid2 size={3.25}>
            <Chip
                label={i18n.setAdditionalData}
                icon={<ArrowOutwardRoundedIcon />}
                onClick={() => window.open(`${usedUrl}#/${link}`, '_blank').focus()}
                clickable
                variant='new-filled'
                sx={{
                    color: mainBlue,
                    backgroundColor: `rgba(${brutRgbMainBlue}, 0.06)`,
                    backgroundColorHover: `rgba(${brutRgbMainBlue}, 0.16)`,
                }}
            />
        </Grid2>
        <Grid2 offset={3.25} size={0.5} />
    </Grid2>
)

ShowDataTypesRules.propTypes = {
    link: PropTypes.string,
}

const ProdReferenceThreshold = ({
    parameters = [],
    setParameters = () => {},
    getGlobalParameter = () => {},
    moduleParameters = [],
    setModuleParameters = [],
    getGlobalModuleParameter = () => {},
}) => {
    const {
        qualityThresholds,
    } = useSelector(store => ({
        qualityThresholds: store.QualityReducer.thresholds,
    }), shallowEqual)

    const parameter = 'PROD_REFERENCE_THRESHOLD'
    const foundParameter = parameters.find(p => p.parameter === parameter)?.value
    const foundModuleParameter = moduleParameters.find(p => p.parameter === parameter)?.value

    const [prodReferenceThreshold, setProdReferenceThreshold] = useState(foundParameter && foundParameter.split(',').map((id) => Number(id)))
    const [moduleProdReferenceThreshold, setModuleProdReferenceThreshold] = useState(foundModuleParameter && foundModuleParameter.split(',').map((id) => Number(id)))

    useDebounce(() => {
        setParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalParameter(parameter, prodReferenceThreshold.join(',')),
        ])
    }, 200, [prodReferenceThreshold])

    useDebounce(() => {
        setModuleParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalModuleParameter(parameter, moduleProdReferenceThreshold.join(',')),
        ])
    }, 200, [moduleProdReferenceThreshold])

    return (
        <Grid2 container justifyContent='space-between' alignItems='center' columnSpacing={1}>
            <Grid2 size={0.5}>
                <HelpParameter image={categoriesCmsDocumentsHelp} />
            </Grid2>
            <Grid2 container size='grow'>
                <Typography variant='normalFormTitle'>{i18n.referenceThresholds}</Typography>
            </Grid2>
            <Grid2 size={3.25}>
                <SuperMultiAutocomplete
                    options={qualityThresholds}
                    values={prodReferenceThreshold}
                    onChange={setProdReferenceThreshold}
                    keyLabel='name'
                    keyValue='code'
                    multiple
                    limit={1}
                />
            </Grid2>
            <Grid2 size={3.25}>
                <SuperMultiAutocomplete
                    options={qualityThresholds}
                    values={moduleProdReferenceThreshold}
                    onChange={setModuleProdReferenceThreshold}
                    keyLabel='name'
                    keyValue='code'
                    multiple
                    limit={1}
                />
            </Grid2>
            <Grid2 size={0.5}>
                <SeeOtherIconTooltip
                    title={i18n.seeQualityThresholds}
                    link={`${usedUrl}#/quality/qualityOptions/threshold`}
                />
            </Grid2>
        </Grid2>
    )
}

ProdReferenceThreshold.propTypes = {
    parameters: PropTypes.arrayOf(PropTypes.shape({})),
    setParameters: PropTypes.func,
    getGlobalParameter: PropTypes.func,
    moduleParameters: PropTypes.arrayOf(PropTypes.shape({})),
    setModuleParameters: PropTypes.func,
    getGlobalModuleParameter: PropTypes.func,
}

const DefaultNGF = ({
    module,
    parameters = [],
    setParameters = () => {},
    getGlobalParameter = () => {},
    moduleParameters = [],
    setModuleParameters = [],
    getGlobalModuleParameter = () => {},
}) => {
    const parameter = `defaultNGF${module.module}`
    const foundParameter = parameters.find(p => p.parameter === parameter)?.value
    const foundModuleParameter = moduleParameters.find(p => p.parameter === parameter)?.value

    const [defaultNGF, setDefaultNGF] = useState(foundParameter === 'true')
    const [moduleDefaultNGF, setModuleDefaultNGF] = useState(foundModuleParameter === 'true')

    useDebounce(() => {
        setParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalParameter(parameter, `${defaultNGF}`),
        ])
    }, 200, [defaultNGF])

    useDebounce(() => {
        setModuleParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalModuleParameter(parameter, `${moduleDefaultNGF}`),
        ])
    }, 200, [moduleDefaultNGF])

    return (
        <Grid2 container justifyContent='space-between' alignItems='flex-start' columnSpacing={1}>
            <Grid2 size={0.5} />
            <Grid2 container size='grow'>
                <Typography variant='normalFormTitle'>{i18n.defaultNGFDisplay}</Typography>
            </Grid2>
            <Grid2 container size={3.25}>
                <Box onClick={() => setDefaultNGF(prev => !prev)}>
                    <Switch checked={defaultNGF} />
                </Box>
            </Grid2>
            <Grid2 container size={3.25}>
                <Box onClick={() => setModuleDefaultNGF(prev => !prev)}>
                    <Switch checked={moduleDefaultNGF} />
                </Box>
            </Grid2>
            <Grid2 size={0.5} />
        </Grid2>
    )
}

DefaultNGF.propTypes = {
    module: PropTypes.string.isRequired,
    parameters: PropTypes.arrayOf(PropTypes.shape({})),
    setParameters: PropTypes.func,
    getGlobalParameter: PropTypes.func,
    moduleParameters: PropTypes.arrayOf(PropTypes.shape({})),
    setModuleParameters: PropTypes.func,
    getGlobalModuleParameter: PropTypes.func,
}

const OTHER_PARAMETERS = ['passwordPolicy', 'securityPasswordDescription', 'supportMailAddress', 'supportPhoneNumber', 'supportContactSchedule', 'managerUrl', 'UDI_REFERENCE_THRESHOLD', 'contributorTypeOperator', 'contributorTypeAdministrator', 'PROD_REFERENCE_THRESHOLD']
const DEFAULT_MODULE = 'SIEAU'
const CMS_CATEGORIES_TO_IGNORE = [-1]
const MODULES_WITH_ACCOUNT_PARAMETERS = [MODULES.find(m => m.id === WIDGET.id)?.name]

const CustomizationParameters = ({
    module = {},
}) => {
    const {
        globalParameters,
        accountUser,
        cmsEvents,
        applicativeUserSettings,
        user,
    } = useSelector(store => ({
        globalParameters: store.AdministrationReducer.globalParameters,
        accountUser: store.AccountReducer.accountUser,
        cmsEvents: store.EventsReducer.cmsEvents,
        applicativeUserSettings: store.AdministrationReducer.applicativeUserSettings,
        user: store.UserReducer.user,
    }), shallowEqual)

    const moduleParams = useContext(ModuleHabsContext)
    const moduleParamsDispatch = useContext(ModuleHabsDispatchContext)

    const globalModuleParameters = [
        ...globalParameters.filter(gp => gp.module === toUpper(module.module)),
        ...globalParameters.filter(gp => OTHER_PARAMETERS.includes(gp.parameter)),
    ]

    const [parameters, setParameters] = useState(moduleParams.parameters.length ? moduleParams.parameters : globalModuleParameters)
    const [moduleParameters, setModuleParameters] = useState(moduleParams.moduleParameters.length ? moduleParams.moduleParameters : applicativeUserSettings.filter(aus => aus.login === module.login))

    useDebounce(() => {
        moduleParamsDispatch({ type: 'parameters', payload: parameters })
    }, 100, [parameters])

    useDebounce(() => {
        moduleParamsDispatch({ type: 'moduleParameters', payload: moduleParameters })
    }, 100, [moduleParameters])

    const getGlobalParameter = (parameter, value) => {
        const foundParam = parameters.find(p => p.parameter === parameter)
        return {
            ...new GlobalParametersDto({
                module: !OTHER_PARAMETERS.includes(parameter) ? toUpper(module.module) : (foundParam?.module || DEFAULT_MODULE),
                parameter,
                value: value || undefined,
                updateDate: moment().valueOf(),
                updateLogin: accountUser.login,
            }),
            isNew: !foundParam?.value,
        }
    }

    const getGlobalModuleParameter = (parameter, value) => {
        const foundParam = moduleParameters.find(p => p.parameter === parameter)
        return {
            ...{
                login: module.login,
                parameter,
                value: value || undefined,
                updateDate: moment().valueOf(),
            },
            isNew: !foundParam?.value,
        }
    }

    const filteredCmsEvents = cmsEvents.filter(ce => !CMS_CATEGORIES_TO_IGNORE.includes(ce.idCategory))
    const defaultProps = {
        parameters,
        setParameters,
        getGlobalParameter,
        moduleParameters,
        setModuleParameters,
        getGlobalModuleParameter,
        cmsEvents: filteredCmsEvents,
        displayModuleParameter: MODULES_WITH_ACCOUNT_PARAMETERS.includes(module.module),
        module,
    }

    const defaultHabsProps = { user, userHabs: moduleParams?.habilitationsUser }

    return (
        <Grid2 size={12}>
            <Stack spacing={1} sx={{ height: '100%' }}>
                {[OBSERVATORY.name, WIDGET.name, WIDGET_CARTO.name].includes(module.module) ? (
                    <Typography variant='sectionTitle'>{i18n.adminModuleCustomizationSubtitle}</Typography>
                ) : (
                    <Typography variant='sectionTitle'>{i18n.adminNoModuleCustomizationSubtitle}</Typography>
                )}
                {module.module === OBSERVATORY.name && (
                    <>
                        <ParameterCard title={i18n.loginPage}>
                            <CmsTitle {...defaultProps} />
                            <CmsHome {...defaultProps} />
                            <CmsInfos {...defaultProps} />
                            <CmsPartners {...defaultProps} />
                            <CmsCGU {...defaultProps} />
                            <RecaptchaId {...defaultProps} />
                            <ContactPageLink {...defaultProps} />
                        </ParameterCard>
                        <ParameterCard title='Widget'>
                            <NotDisplayWidgetHomePage {...defaultProps} />
                            <WidgetPublicToken {...defaultProps} />
                            <ManagerUrl {...defaultProps} />
                        </ParameterCard>
                        <ParameterCard title={i18n.password}>
                            <PasswordPolicy {...defaultProps} />
                            <SecurityPasswordDescription {...defaultProps} />
                        </ParameterCard>
                        <ParameterCard title={i18n.cartography}>
                            <DefaultMapXCoordinate {...defaultProps} />
                            <DefaultMapYCoordinate {...defaultProps} />
                            <DefaultMapZoom {...defaultProps} />
                        </ParameterCard>
                        <ParameterCard title={i18n.documents}>
                            <CmsActualities {...defaultProps} />
                            <CmsDocuments {...defaultProps} />
                            <CmsForum {...defaultProps} />
                            <CmsReponsesForum {...defaultProps} />
                        </ParameterCard>
                        <ParameterCard title={i18n.supportPanel}>
                            <CmsSupport {...defaultProps} />
                            <SupportContactSchedule {...defaultProps} />
                            <SupportPhoneNumber {...defaultProps} />
                            <SupportMailAddress {...defaultProps} />
                        </ParameterCard>
                        <ParameterCard title={i18n.datas}>
                            <WithStats {...defaultProps} />
                            <HabilitationRequired habilitation={H_PIEZO_MODULE} {...defaultHabsProps}>
                                <FieldsetMUI
                                    title={(
                                        <Stack direction='row' alignItems='center' gap={0.5} sx={{ padding: '0 0.5rem' }}>
                                            <ShowChartRoundedIcon />
                                            {i18n.piezometry}
                                        </Stack>
                                    )}
                                >
                                    <Stack spacing={1.25}>
                                        <IdDailyMin {...defaultProps} />
                                        <PiezoValidOnly {...defaultProps} />
                                        <PiezoHorizons {...defaultProps} />
                                        <PiezoLimitHisto {...defaultProps} />
                                        <FrequencyDatas module={toUpper(module.module)} stationType={FREQUENCY_STATIONS_TYPES.piezo} {...defaultProps} />
                                        <ShowDataTypesRules link='piezometry/piezometryOptions' />
                                    </Stack>
                                </FieldsetMUI>
                            </HabilitationRequired>
                            <HabilitationRequired habilitation={H_HYDRO_MODULE} { ...defaultHabsProps }>
                                <FieldsetMUI
                                    title={(
                                        <Stack direction='row' alignItems='center' gap={0.5} sx={{ padding: '0 0.5rem' }}>
                                            <MultilineChartRoundedIcon />
                                            {i18n.hydrometry}
                                        </Stack>
                                    )}
                                >
                                    <Stack spacing={1.25}>
                                        <HydroValidOnly {...defaultProps} />
                                        <HydroHorizons {...defaultProps} />
                                        <HydroLimitHisto {...defaultProps} />
                                        <FrequencyDatas module={toUpper(module.module)} stationType={FREQUENCY_STATIONS_TYPES.hydro} {...defaultProps} />
                                        <ShowDataTypesRules link='hydrometry/hydrometryOptions' />
                                    </Stack>
                                </FieldsetMUI>
                            </HabilitationRequired>
                            <HabilitationRequired habilitation={H_PLUVIO_MODULE} { ...defaultHabsProps }>
                                <FieldsetMUI
                                    title={(
                                        <Stack direction='row' alignItems='center' gap={0.5} sx={{ padding: '0 0.5rem' }}>
                                            <InsertChartOutlinedRoundedIcon />
                                            {i18n.pluviometry}
                                        </Stack>
                                    )}
                                >
                                    <Stack spacing={1.25}>
                                        <PluvioValidOnly {...defaultProps} />
                                        <PluvioHorizons {...defaultProps} />
                                        <PluvioLimitHisto {...defaultProps} />
                                        <FrequencyDatas module={toUpper(module.module)} stationType={FREQUENCY_STATIONS_TYPES.pluvio} {...defaultProps} />
                                        <ShowDataTypesRules link='pluviometry/pluviometryOptions' />
                                    </Stack>
                                </FieldsetMUI>
                            </HabilitationRequired>
                            <HabilitationRequired habilitation={H_QUALITO_MODULE} { ...defaultHabsProps }>
                                <FieldsetMUI
                                    title={(
                                        <Stack direction='row' alignItems='center' gap={0.5} sx={{ padding: '0 0.5rem' }}>
                                            <PieChartRoundedIcon />
                                            {i18n.quality}
                                        </Stack>
                                    )}
                                >
                                    <Stack spacing={1.25}>
                                        <ShowDataTypesRules link='quality/qualityOptions/generalOptions' />
                                    </Stack>
                                </FieldsetMUI>
                            </HabilitationRequired>
                            <HabilitationRequired habilitation={H_PRODUCTION_MODULE} { ...defaultHabsProps }>
                                <FieldsetMUI
                                    title={(
                                        <Stack direction='row' alignItems='center' gap={0.5} sx={{ padding: '0 0.5rem' }}>
                                            <LocationCityRoundedIcon />
                                            {i18n.productionUnitName}
                                        </Stack>
                                    )}
                                >
                                    <Stack spacing={1.25}>
                                        <ProdReferenceThreshold {...defaultProps} />
                                    </Stack>
                                </FieldsetMUI>
                            </HabilitationRequired>
                            <HabilitationRequired habilitation={H_DISTRIBUTION_MODULE} { ...defaultHabsProps }>
                                <FieldsetMUI
                                    title={(
                                        <Stack direction='row' alignItems='center' gap={0.5} sx={{ padding: '0 0.5rem' }}>
                                            <MapRoundedIcon />
                                            {i18n.distributionUnitName}
                                        </Stack>
                                    )}
                                >
                                    <Stack spacing={1.25}>
                                        <UdiReferenceThreshold {...defaultProps} />
                                        <ContributorTypeOperator {...defaultProps} />
                                        <ContributorTypeAdministrator {...defaultProps} />
                                    </Stack>
                                </FieldsetMUI>
                            </HabilitationRequired>
                            <HabilitationRequired habilitation={H_INSTALLATION_MODULE} { ...defaultHabsProps }>
                                <FieldsetMUI
                                    title={(
                                        <Stack direction='row' alignItems='center' gap={0.5} sx={{ padding: '0 0.5rem' }}>
                                            <BuildRoundedIcon />
                                            {i18n.installation}
                                        </Stack>
                                    )}
                                >
                                    <Stack spacing={1.25}>
                                        <ShowDataTypesRules link='installation/installationOptions' />
                                    </Stack>
                                </FieldsetMUI>
                            </HabilitationRequired>
                        </ParameterCard>
                    </>
                )}
                {[WIDGET.name, WIDGET_CARTO.name].includes(module.module) && (
                    <>
                        <ParameterCard title={i18n.datas}>
                            <WithStats {...defaultProps} isWidget />
                            <DefaultNGF {...defaultProps} />
                            <HabilitationRequired habilitation={H_PIEZO_MODULE} { ...defaultHabsProps }>
                                <FieldsetMUI
                                    title={(
                                        <Stack direction='row' alignItems='center' gap={0.5} sx={{ padding: '0 0.5rem' }}>
                                            <ShowChartRoundedIcon />
                                            {i18n.piezometry}
                                        </Stack>
                                    )}
                                >
                                    <Stack spacing={1.25}>
                                        <PiezoValidOnly {...defaultProps} isWidget />
                                        <PiezoHorizons {...defaultProps} isWidget />
                                        <PiezoLimitHisto {...defaultProps} isWidget />
                                        <FrequencyDatas stationType={FREQUENCY_STATIONS_TYPES.piezo} {...defaultProps} />
                                    </Stack>
                                </FieldsetMUI>
                            </HabilitationRequired>
                            <HabilitationRequired habilitation={H_HYDRO_MODULE} { ...defaultHabsProps }>
                                <FieldsetMUI
                                    title={(
                                        <Stack direction='row' alignItems='center' gap={0.5} sx={{ padding: '0 0.5rem' }}>
                                            <MultilineChartRoundedIcon />
                                            {i18n.hydrometry}
                                        </Stack>
                                    )}
                                >
                                    <Stack spacing={1.25}>
                                        <HydroValidOnly {...defaultProps} isWidget />
                                        <HydroHorizons {...defaultProps} isWidget />
                                        <HydroLimitHisto {...defaultProps} isWidget />
                                        <FrequencyDatas stationType={FREQUENCY_STATIONS_TYPES.hydro} {...defaultProps} />
                                    </Stack>
                                </FieldsetMUI>
                            </HabilitationRequired>
                            <HabilitationRequired habilitation={H_PLUVIO_MODULE} { ...defaultHabsProps }>
                                <FieldsetMUI
                                    title={(
                                        <Stack direction='row' alignItems='center' gap={0.5} sx={{ padding: '0 0.5rem' }}>
                                            <InsertChartOutlinedRoundedIcon />
                                            {i18n.pluviometry}
                                        </Stack>
                                    )}
                                >
                                    <Stack spacing={1.25}>
                                        <PluvioValidOnly {...defaultProps} isWidget />
                                        <PluvioHorizons {...defaultProps} isWidget />
                                        <PluvioLimitHisto {...defaultProps} isWidget />
                                        <FrequencyDatas stationType={FREQUENCY_STATIONS_TYPES.pluvio} {...defaultProps} />
                                    </Stack>
                                </FieldsetMUI>
                            </HabilitationRequired>
                        </ParameterCard>
                    </>
                )}
                {![OBSERVATORY.name, WIDGET.name, WIDGET_CARTO.name].includes(module.module) && (
                    <Card variant='newCard' sx={{ flex: 1, overflow: 'auto', height: '100%', textAlign: 'center' }}>
                        <CardContent sx={{ height: '100%', '& object': { height: '100%', width: 'auto' } }}>
                            <object data={noData} type='image/svg+xml' />
                        </CardContent>
                    </Card>
                )}
            </Stack>
        </Grid2>
    )
}
CustomizationParameters.propTypes = {
    module: PropTypes.shape({
        login: PropTypes.string,
        module: PropTypes.string,
        moduleTypeId: PropTypes.number,
    }),
}

export default CustomizationParameters